import { useEffect, useMemo, useState } from "react";
import type { PvModuleSpec } from "@pv/engine";
import {
  api, ApiError, downloadFile, geocode,
  type CatalogueBattery, type CatalogueInverter, type CatalogueModule,
  type AutoDesignResponse, type CatalogueEms, type CatalogueEvCharger, type CatalogueHeatPump, type CatalogueMeter,
  type CatalogueMicroinverter, type CatalogueOptimizer, type GeoProviderEval, type MlpeAdvice, type PaidDataRecommendation,
  type DesignCandidate, type InstallationView, type ProcurementOrder, type Supplier,
  type InstallationPackage, type ProjectDetail, type SimulationResponse, type SurfaceDto,
} from "./api.js";
import SiteCanvas, { fitViewFor, type CanvasGeometry, type Tool, type View } from "./SiteCanvas.js";
import type { ClientBasemap } from "./basemapClient.js";
import { postcodeMismatch, resolvedBundesland } from "./geocodeUtils.js";
import { BarChart, CashflowChart } from "./charts.js";
import { useLocale, useT, type MessageKey } from "./i18n/index.js";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const STATUSES = ["FEASIBILITY", "DESIGN", "TECHNICAL_REVIEW", "PROPOSAL_SENT", "ACCEPTED", "PERMITTING", "PROCUREMENT", "INSTALLATION_PLANNED", "INSTALLING", "COMMISSIONING", "OPERATING", "COMPLETED"];
const STATES = ["Baden-Württemberg", "Bayern", "Berlin", "Brandenburg", "Bremen", "Hamburg", "Hessen", "Mecklenburg-Vorpommern", "Niedersachsen", "Nordrhein-Westfalen", "Rheinland-Pfalz", "Saarland", "Sachsen", "Sachsen-Anhalt", "Schleswig-Holstein", "Thüringen"];

type StepId = "overview" | "site" | "design" | "package" | "results" | "permits" | "proposals" | "procurement" | "installation";
const STEPS: { id: StepId; icon: string; labelKey: MessageKey; sectKey?: MessageKey }[] = [
  { id: "overview", icon: "📋", labelKey: "proj.step.overview", sectKey: "proj.sect.plan" },
  { id: "site", icon: "🗺️", labelKey: "proj.step.site" },
  { id: "design", icon: "🔌", labelKey: "proj.step.design" },
  { id: "package", icon: "🧩", labelKey: "proj.step.package", sectKey: "proj.sect.configure" },
  { id: "results", icon: "⚡", labelKey: "proj.step.results" },
  { id: "permits", icon: "📑", labelKey: "proj.step.permits", sectKey: "proj.sect.deliver" },
  { id: "proposals", icon: "📄", labelKey: "proj.step.proposals" },
  { id: "procurement", icon: "🛒", labelKey: "proj.step.procurement", sectKey: "proj.sect.execute" },
  { id: "installation", icon: "🔧", labelKey: "proj.step.installation" },
];

const fmt = (n: number, d = 0) => n.toLocaleString("de-DE", { maximumFractionDigits: d, minimumFractionDigits: d });
const eur = (n: number) => `${fmt(Math.round(n))} €`;

/* ------------------------- E47: export gating + downloads ------------------------- */

/** Why exports are gated for this viewer, if at all — fetched once per workspace. */
export interface ExportGate {
  demo: boolean; // org in DEMO_EVALUATION → exports locked
  unverified: boolean; // viewer's email not verified → exports locked (E40)
}

/**
 * E47 — THE download path. Every user-triggered download goes through this
 * wrapper: rejections surface the API's own localized, claim-lock-compliant
 * explanation in the panel's message area. A void-swallowed download promise at
 * a call site is the bug this replaces (guard-tested: none may remain).
 */
function makeDl(setNote: (s: string) => void) {
  return (path: string, filename: string): void => {
    downloadFile(path, filename).catch((e) => setNote(e instanceof Error ? e.message : String(e)));
  };
}

/** E47 — gated actions look gated BEFORE the click: banner with reason + remedy. */
function ExportGateBanner({ gate }: { gate: ExportGate | null }): JSX.Element | null {
  const t = useT();
  const [sent, setSent] = useState(false);
  if (!gate || (!gate.demo && !gate.unverified)) return null;
  return (
    <div className="msg warn" data-testid="export-gate-banner">
      {gate.unverified ? t("gate.unverified") : t("gate.demo")}
      {gate.unverified && (
        <>
          {" "}
          <button
            className="btn btn-sm"
            disabled={sent}
            onClick={() => void api.resendVerificationSelf().then(() => setSent(true)).catch(() => setSent(false))}
          >
            {t("gate.resend")}
          </button>
          {sent && <span style={{ marginLeft: 8 }}>{t("gate.resendSent")}</span>}
        </>
      )}
    </div>
  );
}

/** Reason shown on a disabled export button (tooltip + label suffix). */
function gateRemedy(gate: ExportGate | null, t: (k: MessageKey) => string): string | null {
  if (!gate) return null;
  if (gate.unverified) return t("gate.unverifiedRemedy");
  if (gate.demo) return t("gate.demoRemedy");
  return null;
}

function toModuleSpec(m: CatalogueModule): PvModuleSpec {
  return {
    id: m.id, manufacturer: m.manufacturer, model: m.model, wp: m.wp,
    widthM: m.widthM, heightM: m.heightM, efficiencyPct: m.efficiencyPct,
    cellTechnology: m.cellTechnology, bifacial: m.bifacial,
    gammaPmaxPctPerK: m.gammaPmaxPctPerK, vocV: m.vocV, betaVocPctPerK: m.betaVocPctPerK,
    vmppV: m.vmppV, imppA: m.imppA, iscA: m.iscA,
    degradationPctPerYear: m.degradationPctPerYear, firstYearDegradationPct: m.firstYearDegradationPct,
    warrantyProductYears: m.warrantyProductYears, warrantyPerformanceYears: m.warrantyPerformanceYears,
    priceEur: m.listPriceEur,
  };
}

export default function ProjectWorkspace({ projectId, onBack }: { projectId: string; onBack: () => void }): JSX.Element {
  const t = useT();
  const [step, setStep] = useState<StepId>("overview");
  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [catalogue, setCatalogue] = useState<{ modules: CatalogueModule[]; inverters: CatalogueInverter[]; batteries: CatalogueBattery[]; evChargers: CatalogueEvCharger[]; heatPumps: CatalogueHeatPump[]; ems: CatalogueEms[]; meters: CatalogueMeter[]; microinverters: CatalogueMicroinverter[]; optimizers: CatalogueOptimizer[] } | null>(null);
  const [sim, setSim] = useState<SimulationResponse | null>(null);
  const [error, setError] = useState("");
  // E47: gating facts fetched once — export buttons look gated before the click
  const [gate, setGate] = useState<ExportGate | null>(null);

  async function reload(): Promise<void> {
    try {
      setDetail(await api.projects.detail(projectId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }
  useEffect(() => {
    void reload();
    api.components().then(setCatalogue).catch((e) => setError(String(e)));
    api
      .org()
      .then((o) => setGate({ demo: o.effectiveAccessMode === "DEMO_EVALUATION", unverified: !o.viewerEmailVerified }))
      .catch(() => setGate(null));
  }, [projectId]);

  if (!detail || !catalogue) {
    return <div style={{ padding: 40 }}>{error ? <div className="msg err">{error}</div> : <span className="spinner" />}</div>;
  }

  const r = sim?.result;
  const done: Record<StepId, boolean> = {
    overview: true,
    site: !!detail.site && detail.surfaces.length > 0,
    design: detail.designs.length > 0,
    package: detail.designs.length > 0,
    results: !!sim,
    permits: detail.permitTasks.length > 0,
    procurement: false,
    installation: false,
    proposals: detail.proposals.length > 0,
  };

  return (
    <>
      <div className="proj">
        <nav className="sidenav">
          {STEPS.map((s) => (
            <div key={s.id}>
              {s.sectKey && <div className="sect">{t(s.sectKey)}</div>}
              <div className={`step ${step === s.id ? "active" : ""} ${done[s.id] && step !== s.id ? "done" : ""}`} onClick={() => setStep(s.id)}>
                <span className="ic">{s.icon}</span>
                {t(s.labelKey)}
              </div>
            </div>
          ))}
          <div style={{ padding: "18px 16px" }}>
            <button className="btn btn-sm" onClick={onBack}>{t("proj.allProjects")}</button>
          </div>
        </nav>
        <main className="stage">
          {error && <div className="msg err">{error}</div>}
          {step === "overview" && <Overview detail={detail} sim={sim} onReload={reload} onGoto={setStep} />}
          {step === "site" && <SitePanel detail={detail} onReload={reload} onNext={() => setStep("design")} />}
          {step === "design" && <DesignPanel detail={detail} catalogue={catalogue} onReload={reload} onNext={() => setStep("package")} />}
          {step === "package" && <PackagePanel detail={detail} onReload={reload} onNext={() => setStep("results")} />}
          {step === "results" && <ResultsPanel detail={detail} sim={sim} setSim={setSim} setError={setError} gate={gate} onNext={() => setStep("permits")} />}
          {step === "permits" && <PermitsPanel detail={detail} onReload={reload} gate={gate} onNext={() => setStep("proposals")} />}
          {step === "proposals" && <ProposalsPanel detail={detail} sim={sim} onReload={reload} gate={gate} />}
          {step === "procurement" && <ProcurementPanel detail={detail} gate={gate} onNext={() => setStep("installation")} />}
          {step === "installation" && <InstallationPanel detail={detail} onReload={reload} />}
        </main>
      </div>
      <footer className="rollup">
        <div className="ri"><b>{r ? fmt(r.layout.dcKwp, 1) : "–"}</b><span>kWp DC</span></div>
        <div className="ri"><b>{r ? r.layout.moduleCount : "–"}</b><span>{t("proj.rollup.modules")}</span></div>
        <div className="ri"><b>{r ? fmt(r.energy.annualAcKwh) : "–"}</b><span>{t("proj.rollup.kwhYear")}</span></div>
        <div className="ri"><b>{r ? eur(r.finance.investmentEur) : "–"}</b><span>{t("proj.rollup.investment")}</span></div>
        <div className="ri"><b>{r?.finance.paybackYears ? `${fmt(r.finance.paybackYears, 1)} ${t("proj.yearsShort")}` : "–"}</b><span>{t("proj.rollup.payback")}</span></div>
        <div className="ri"><b>{r ? fmt(r.co2SavedTonsPerYear, 1) : "–"}</b><span>t CO₂ / a</span></div>
        <div style={{ flex: 1 }} />
        <label style={{ fontSize: 11, color: "var(--muted-2)" }}>{t("proj.rollup.status")}</label>
        <select value={detail.project.status} onChange={(e) => void api.projects.update(projectId, { status: e.target.value }).then(reload)}>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`pstatus.${s}` as MessageKey)}</option>)}
        </select>
      </footer>
    </>
  );
}

/* ------------------------------- Overview -------------------------------- */

function Overview({ detail, sim, onReload, onGoto }: {
  detail: ProjectDetail; sim: SimulationResponse | null; onReload: () => Promise<void>; onGoto: (s: StepId) => void;
}): JSX.Element {
  const t = useT();
  const p = detail.project;
  const r = sim?.result;
  return (
    <div>
      <h1>{p.name}</h1>
      <div className="sub">{detail.site ? `${detail.site.addressLine ?? ""}, ${detail.site.city ?? ""} · ${detail.site.state ?? ""}` : t("proj.ov.noSite")} · {t(`ptype.${p.projectType}` as MessageKey)}</div>
      <div className="tiles">
        <div className="tile hl"><div className="v">{r ? `${fmt(r.layout.dcKwp, 1)} kWp` : "—"}</div><div className="l">{t("proj.ov.systemSize")} {r ? `(${r.layout.moduleCount} ${t("proj.ov.modulesSuffix")})` : t("proj.ov.simulateToCompute")}</div></div>
        <div className="tile"><div className="v">{r ? `${fmt(r.energy.annualAcKwh)} kWh` : "—"}</div><div className="l">{t("proj.ov.annualProduction")}</div></div>
        <div className="tile"><div className="v">{r ? eur(r.finance.investmentEur) : "—"}</div><div className="l">{t("proj.ov.investmentNet")}</div></div>
        <div className="tile"><div className="v">{r?.finance.paybackYears ? `${fmt(r.finance.paybackYears, 1)} ${t("proj.yearsShort")}` : "—"}</div><div className="l">{t("proj.ov.payback")}</div></div>
      </div>
      <div className="card" style={{ marginBottom: 14 }}>
        <h3 style={{ marginBottom: 10 }}>{t("proj.ov.progress")}</h3>
        {([
          [t("proj.ov.siteDrawn"), detail.surfaces.length > 0, "site"],
          [t("proj.ov.designConfigured"), detail.designs.length > 0, "design"],
          [t("proj.ov.simulationRun"), !!sim, "results"],
          [t("proj.ov.regChecklist"), detail.permitTasks.length > 0, "permits"],
          [t("proj.ov.proposalCreated"), detail.proposals.length > 0, "proposals"],
        ] as [string, boolean, StepId][]).map(([label, ok, target], i) => (
          <div key={i} className={`check ${ok ? "donec" : ""}`} style={{ cursor: "pointer" }} onClick={() => onGoto(target)}>
            <div className="cb">{ok ? "✓" : ""}</div>
            <div className="t"><b>{label}</b><span>{ok ? t("proj.ov.done") : t("proj.ov.open")}</span></div>
          </div>
        ))}
      </div>
      <button className="btn btn-primary" onClick={() => onGoto("site")}>{t("proj.ov.continueSite")}</button>
    </div>
  );
}

/* ------------------------------ Site panel ------------------------------- */

interface EditableSurface {
  id: string; // srf-<n> for unsaved
  persistedId: string | null;
  name: string;
  kind: string;
  geometry: CanvasGeometry;
  azimuthDeg: number;
  tiltDeg: number;
  edgeSetbackM: number;
  obstacleBufferM: number;
  gridRotationDeg: number;
}

function toEditable(s: SurfaceDto): EditableSurface {
  return {
    id: s.id,
    persistedId: s.id,
    name: s.name,
    kind: s.kind,
    geometry: { vertices: s.boundaryLocal.vertices, obstacles: s.obstacles },
    azimuthDeg: s.azimuthDeg,
    tiltDeg: s.tiltDeg,
    edgeSetbackM: s.edgeSetbackM,
    obstacleBufferM: s.obstacleBufferM,
    gridRotationDeg: s.gridRotationDeg ?? 0,
  };
}

function GeoSourcesCard({ detail, onReload }: { detail: ProjectDetail; onReload: () => Promise<void> }): JSX.Element {
  const t = useT();
  const [evals, setEvals] = useState<GeoProviderEval[] | null>(null);
  const [decision, setDecision] = useState("");
  const [busy, setBusy] = useState(false);
  const [paidRec, setPaidRec] = useState<PaidDataRecommendation | null>(null);
  const [approval, setApproval] = useState("");

  useEffect(() => {
    api.projects.dataRecommendation(detail.project.id).then((r) => setPaidRec(r.recommendation)).catch(() => undefined);
  }, [detail.project.id]);

  async function load(): Promise<void> {
    setBusy(true);
    try {
      const r = await api.projects.twinProviders(detail.project.id);
      setEvals(r.evaluated);
      setDecision(r.decision);
    } finally {
      setBusy(false);
    }
  }

  async function runPipeline(): Promise<void> {
    setBusy(true);
    try {
      const r = await api.projects.twinReconstruct(detail.project.id, { apply: true });
      setEvals(r.evaluated);
      setDecision(r.decision);
      if (r.surfacesCreated.length > 0) await onReload();
    } finally {
      setBusy(false);
    }
  }

  const badge = (e: GeoProviderEval): string =>
    e.available ? "chip ok" : e.status === "commercial_stub" ? "chip" : "chip info";
  const costLabel = (e: GeoProviderEval): string =>
    e.cost.model === "free_open_data" ? t("geo.openData")
    : e.cost.model === "included_project_upload" ? t("geo.ownData")
    : e.cost.model === "free_api_quota" ? t("geo.freeQuota")
    : e.cost.model === "per_report" ? `~${e.cost.estimatedEurPerSite ?? "?"} ${t("geo.perReport")}`
    : e.cost.model === "subscription" ? t("geo.subscription")
    : t("geo.quote");

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <h3 style={{ marginBottom: 4 }}>{t("geo.title")}</h3>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        {t("geo.hint")}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn btn-o btn-sm" disabled={busy} onClick={() => void load()}>{busy ? <span className="spinner" /> : t("geo.evaluate")}</button>
        <button className="btn btn-sm" disabled={busy} onClick={() => void runPipeline()}>{t("geo.reconstruct")}</button>
      </div>
      {decision && <div className="msg info" style={{ marginTop: 8 }}>{decision}</div>}
      {paidRec && (
        <div className="card" style={{ marginTop: 10, borderLeft: "3px solid var(--warn)" }}>
          <b>{t("geo.higherAccuracy")} {paidRec.providerName}</b>
          <div className="muted" style={{ fontSize: 12, margin: "6px 0" }}>{paidRec.why}</div>
          <div style={{ fontSize: 12.5 }}>
            {t("geo.estimatedCost")} <b>{paidRec.estimatedCostEur != null ? `${paidRec.estimatedCostEur} €` : paidRec.costModel}</b> ·
            {" "}{t("geo.expectedBenefit")} <b>{paidRec.currentConfidencePct} % → {paidRec.expectedConfidencePct} %</b> ·
            {" "}{paidRec.necessity === "optional" ? t("geo.optional") : t("geo.recommended")}
          </div>
          <button
            className="btn btn-sm"
            style={{ marginTop: 8 }}
            onClick={() => {
              void api.projects.approvePaidData(detail.project.id, paidRec.providerId).then((r) => setApproval(r.note));
            }}
          >
            {t("geo.approvePurchase")}
          </button>
          {approval && <div className="msg info" style={{ marginTop: 6 }}>{approval}</div>}
          <div className="muted" style={{ fontSize: 10.5, marginTop: 4 }}>{t("geo.approvalNote")}</div>
        </div>
      )}
      {evals && (
        <table className="tbl" style={{ marginTop: 8 }}>
          <thead><tr><th>{t("geo.col.source")}</th><th>{t("geo.col.status")}</th><th className="r">{t("geo.col.cost")}</th></tr></thead>
          <tbody>
            {evals.map((e) => (
              <tr key={e.providerId}>
                <td><b>{e.name}</b><br /><span className="muted" style={{ fontSize: 10.5 }}>{e.coverageNote} · {e.cost.licensingNote}</span></td>
                <td><span className={badge(e)}>{e.available ? t("geo.available") : e.status === "commercial_stub" ? t("geo.needsContract") : e.status === "coverage_directory" ? t("geo.coming") : t("geo.noData")}</span></td>
                <td className="r" style={{ whiteSpace: "nowrap" }}>{costLabel(e)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SitePanel({ detail, onReload, onNext }: { detail: ProjectDetail; onReload: () => Promise<void>; onNext: () => void }): JSX.Element {
  const t = useT();
  const pid = detail.project.id;
  const defaultKind = detail.project.projectType.includes("FLAT")
    ? "flat_roof"
    : detail.project.projectType === "GROUND_MOUNTED"
      ? "ground"
      : "pitched_roof";

  const [address, setAddress] = useState(detail.site?.addressLine ?? "");
  const [state, setState] = useState(detail.site?.state ?? "Bayern");
  const [lat, setLat] = useState(detail.site?.lat ?? 48.137);
  const [lon, setLon] = useState(detail.site?.lon ?? 11.575);
  const [cons, setCons] = useState(detail.site?.annualConsumptionKwh ?? 4500);
  const [price, setPrice] = useState(detail.site?.electricityPriceCtKwh ?? 37);
  const [profile, setProfile] = useState(detail.site?.loadProfile ?? "H0");

  const [surfaces, setSurfaces] = useState<EditableSurface[]>(() =>
    detail.surfaces.length > 0
      ? detail.surfaces.map(toEditable)
      : [{ id: "srf-1", persistedId: null, name: `${t("site.roofN")} 1`, kind: defaultKind, geometry: { vertices: [], obstacles: [] }, azimuthDeg: 180, tiltDeg: 35, edgeSetbackM: 0.3, obstacleBufferM: 0.25, gridRotationDeg: 0 }],
  );
  const [deleted, setDeleted] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string>(surfaces[0]?.id ?? "srf-1");
  const [view, setView] = useState<View>(fitViewFor(surfaces));
  const [tool, setTool] = useState<Tool>("draw");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  // E69 — typed-vs-resolved postcode mismatch (visible warning, not small print)
  const [plzWarning, setPlzWarning] = useState<{ typed: string; resolved: string } | null>(null);
  // E59 — per-Bundesland official basemap; null → Esri (degrade, don't break).
  // E61 — refetch with the coordinates ON SCREEN: a fresh project has no saved
  // site, so a fetch keyed only on project.id showed Esri for a geocoded
  // München address until save+reload — the feature was never asked for.
  // Official imagery now engages as soon as the location is known.
  const [basemap, setBasemap] = useState<ClientBasemap | null>(null);
  useEffect(() => {
    api.projects.basemap(detail.project.id, { lat, lon, state }).then((r) => setBasemap(r.active)).catch(() => setBasemap(null));
  }, [detail.project.id, lat, lon, state]);
  // E54: ambiguous detection → the planner picks the building on the imagery
  const [candidates, setCandidates] = useState<
    { buildingId: string; distanceM: number; roofAreaM2: number; planeCount: number; outlineLocal: { x: number; y: number }[] }[] | null
  >(null);

  const active = surfaces.find((s) => s.id === activeId) ?? surfaces[0]!;

  function patchActive(patch: Partial<EditableSurface>): void {
    setSurfaces((all) => all.map((s) => (s.id === active.id ? { ...s, ...patch } : s)));
  }

  function addSurface(): void {
    const n = surfaces.length + 1;
    const id = `srf-new-${n}-${Date.now() % 10000}`;
    setSurfaces((all) => [...all, { id, persistedId: null, name: `${t("site.roofN")} ${n}`, kind: defaultKind, geometry: { vertices: [], obstacles: [] }, azimuthDeg: 180, tiltDeg: 35, edgeSetbackM: 0.3, obstacleBufferM: 0.25, gridRotationDeg: 0 }]);
    setActiveId(id);
    setTool("draw");
  }

  function removeSurface(id: string): void {
    const s = surfaces.find((x) => x.id === id);
    if (!s) return;
    if (s.persistedId) setDeleted((d) => [...d, s.persistedId!]);
    const rest = surfaces.filter((x) => x.id !== id);
    setSurfaces(rest);
    if (activeId === id && rest[0]) setActiveId(rest[0].id);
  }

  async function detect(provider: "auto" | "google_solar", buildingId?: string): Promise<void> {
    setBusy(true);
    // E65 constraint 2 — VISIBLE loading: a first-time official tile fetch can
    // take ~20 s (51 MB from the Land server). The button is disabled via
    // `busy`; this line says what is happening instead of a mute spinner.
    setNote(provider === "auto" ? t("site.detectLoading") : "");
    try {
      // Persist the site first so detection uses the current pin
      await api.projects.putSite(pid, {
        addressLine: address, state, lat, lon,
        annualConsumptionKwh: cons, loadProfile: profile, electricityPriceCtKwh: price,
      });
      // E61: site persisted → saved-site basemap decision (records DG rule 3)
      api.projects.basemap(pid).then((r) => setBasemap(r.active)).catch(() => undefined);
      const res = await api.projects.detectSurfaces(pid, provider, buildingId);
      // E54: honest ambiguity — draw the candidate outlines on the imagery and ask
      if (res.requiresSelection && res.candidates) {
        setCandidates(res.candidates);
        const candSurfaces: EditableSurface[] = res.candidates.map((cd, i) => ({
          id: `cand-${cd.buildingId}`,
          persistedId: null,
          name: `${t("site.candidate")} ${i + 1}`,
          kind: "pitched_roof",
          geometry: { vertices: cd.outlineLocal, obstacles: [] },
          azimuthDeg: 180, tiltDeg: 35, edgeSetbackM: 0.3, obstacleBufferM: 0.25, gridRotationDeg: 0,
        }));
        setSurfaces((all) => [...all.filter((s) => s.persistedId || s.geometry.vertices.length >= 3), ...candSurfaces]);
        if (candSurfaces[0]) setActiveId(candSurfaces[0].id);
        setView(fitViewFor(candSurfaces));
        setNote(t("site.pickBuilding"));
        return;
      }
      setCandidates(null);
      // a confirmed pick replaces the temporary candidate outlines
      if (buildingId) setSurfaces((all) => all.filter((s) => !s.id.startsWith("cand-")));
      const imported = (res.surfaces ?? []).map(toEditable);
      setSurfaces((all) => {
        // drop empty unsaved placeholders AND temporary candidate outlines, keep drawn ones
        const kept = all.filter((x) => (x.persistedId || x.geometry.vertices.length >= 3) && !x.id.startsWith("cand-"));
        return [...kept, ...imported];
      });
      if (imported[0]) setActiveId(imported[0].id);
      setView(fitViewFor([...surfaces, ...imported]));
      // E66: the banner names the source the API ACTUALLY answered with — the
      // request parameter is not the result. Romina's four official planes were
      // captioned "OpenStreetMap footprint" because this line read `provider`.
      const srcKey =
        res.provider === "official_geodata"
          ? "site.detectedOfficial"
          : res.provider === "google_solar"
            ? "site.detectedGoogle"
            : "site.detectedOsm";
      // E67: say why the better rungs didn't answer — the planner should know
      // official geometry was tried (or unavailable) without asking us
      const reasons = (res.sourceChain ?? [])
        .map((x) =>
          x.source === "official_geodata" && (x.outcome === "no_tile" || x.outcome === "no_tile_source")
            ? t("site.chainOfficialNoTile")
            : x.source === "official_geodata" && x.outcome === "not_ready"
              ? t("site.chainOfficialNotReady")
              : x.source === "google_solar" && x.outcome === "not_configured"
                ? t("site.chainGoogleNotConfigured")
                : x.source === "google_solar" && x.outcome === "no_coverage"
                  ? t("site.chainGoogleNoCoverage")
                  : null,
        )
        .filter((x): x is string => x !== null);
      setNote(`${t("site.detected", { n: imported.length, src: t(srcKey) })}${reasons.length ? ` · ${reasons.join(" · ")}` : ""}`);
      await onReload();
    } catch (e) {
      // E41: a 503 means "detection unavailable" — show the calm LOCALIZED fallback
      // line (DE cross-pinned to the API's DETECT_FALLBACK_MESSAGE; EN its approved
      // translation), never an alarming "Error:"-prefixed failure. Manual drawing stays open.
      if (e instanceof ApiError && e.status === 503) setNote(t("site.detectUnavailable"));
      else setNote(`${t("common.error")}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function findAddress(): Promise<void> {
    if (!address.trim()) return;
    const g = await geocode(address);
    if (g) {
      setLat(g.lat);
      setLon(g.lon);
      // E69: the RESOLVED address drives the Bundesland — Stuttgart under a
      // stale "Bayern" dropdown broke the regulatory step, not detection.
      const bl = resolvedBundesland(g.state, g.city);
      if (bl) setState(bl);
      // E69: wrong-Marktplatz geocodes get a VISIBLE warning naming both
      // postcodes — the geocoder resolved 70372 for a typed 70173.
      setPlzWarning(postcodeMismatch(address, g.postcode));
      setNote(`📍 ${g.label}`);
    } else {
      setPlzWarning(null);
      setNote(t("site.addressNotFound"));
    }
  }

  async function save(): Promise<void> {
    setBusy(true);
    setNote("");
    try {
      await api.projects.putSite(pid, {
        addressLine: address, state, lat, lon,
        annualConsumptionKwh: cons, loadProfile: profile, electricityPriceCtKwh: price,
      });
      // E61: the site is saved now — a coordinate-free fetch serves the SAVED
      // site's decision and records it in the Decision Graph (rule 3)
      api.projects.basemap(pid).then((r) => setBasemap(r.active)).catch(() => undefined);
      for (const delId of deleted) await api.projects.deleteSurface(pid, delId);
      for (const s of surfaces) {
        if (s.geometry.vertices.length < 3) continue;
        const payload = {
          name: s.name,
          kind: s.kind,
          boundaryLocal: { vertices: s.geometry.vertices },
          obstacles: s.geometry.obstacles,
          azimuthDeg: s.azimuthDeg,
          tiltDeg: s.tiltDeg,
          edgeSetbackM: s.edgeSetbackM,
          obstacleBufferM: 0.25,
          gridRotationDeg: s.gridRotationDeg,
        };
        if (s.persistedId) await api.projects.updateSurface(pid, s.persistedId, payload);
        else await api.projects.addSurface(pid, payload as Omit<SurfaceDto, "id">);
      }
      setDeleted([]);
      await onReload();
      setNote(t("site.saved"));
    } catch (e) {
      setNote(`${t("common.error")}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1>{t("site.title")}</h1>
      <div className="sub">{t("site.hint")}</div>
      <div className="frow" style={{ marginBottom: 12 }}>
        <div className="field" style={{ flex: 2 }}>
          <label>{t("site.address")}</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input style={{ flex: 1 }} value={address} onChange={(e) => setAddress(e.target.value)} placeholder={t("site.addressPlaceholder")} onKeyDown={(e) => e.key === "Enter" && void findAddress()} />
            <button className="btn btn-o" onClick={() => void findAddress()}>{t("site.find")}</button>
          </div>
        </div>
        <div className="field"><label>{t("site.state")}</label>
          <select value={state} onChange={(e) => setState(e.target.value)}>{STATES.map((s) => <option key={s}>{s}</option>)}</select>
        </div>
        <div className="field"><label>{t("site.consumption")}</label><input type="number" value={cons} onChange={(e) => setCons(+e.target.value)} /></div>
        <div className="field"><label>{t("site.elPrice")}</label><input type="number" step={0.5} value={price} onChange={(e) => setPrice(+e.target.value)} /></div>
        <div className="field"><label>{t("site.loadProfile")}</label>
          <select value={profile} onChange={(e) => setProfile(e.target.value)}>
            <option value="H0">{t("site.profile.H0")}</option>
            <option value="G0">{t("site.profile.G0")}</option>
            <option value="G1">{t("site.profile.G1")}</option>
            <option value="L0">{t("site.profile.L0")}</option>
          </select>
        </div>
      </div>

      {/* Surface manager */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        {surfaces.map((s) => (
          <span key={s.id} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <button className={`btn btn-sm ${s.id === activeId ? "active" : ""}`} onClick={() => setActiveId(s.id)}>
              {s.name} {s.geometry.vertices.length > 2 ? "✓" : "✏️"}
            </button>
            {surfaces.length > 1 && (
              <button className="btn btn-sm" title={t("site.deleteSurface")} onClick={() => removeSurface(s.id)}>✕</button>
            )}
          </span>
        ))}
        <button className="btn btn-sm btn-o" onClick={addSurface}>{t("site.addSurface")}</button>
        <button className="btn btn-sm btn-o" disabled={busy} onClick={() => void detect("auto")} title={t("site.detectTitle")}>{t("site.detectBuilding")}</button>
        <button className="btn btn-sm btn-o" disabled={busy} onClick={() => void detect("google_solar")} title={t("site.aiTitle")}>{t("site.aiSegments")}</button>
        <input style={{ width: 140 }} value={active.name} onChange={(e) => patchActive({ name: e.target.value })} title={t("site.surfaceName")} />
        <select value={active.kind} onChange={(e) => patchActive({ kind: e.target.value })}>
          <option value="pitched_roof">{t("site.kind.pitched_roof")}</option>
          <option value="flat_roof">{t("site.kind.flat_roof")}</option>
          <option value="ground">{t("site.kind.ground")}</option>
          <option value="carport">{t("site.kind.carport")}</option>
          <option value="facade">{t("site.kind.facade")}</option>
        </select>
      </div>

      <div className="canvas-wrap">
        <div className="canvas-tools">
          <button className={`btn btn-sm ${tool === "draw" ? "active" : ""}`} onClick={() => setTool("draw")}>{t("site.drawBoundary")}</button>
          <button className={`btn btn-sm ${tool === "obstacle" ? "active" : ""}`} onClick={() => setTool("obstacle")}>{t("site.obstacle")}</button>
          <span className="sep" />
          <div className="slider-field">{t("site.azimuth")} <input type="range" min={90} max={270} step={5} value={active.azimuthDeg} onChange={(e) => patchActive({ azimuthDeg: +e.target.value })} /><b>{active.azimuthDeg}°</b></div>
          <div className="slider-field">{t("site.tilt")} <input type="range" min={0} max={60} value={active.tiltDeg} onChange={(e) => patchActive({ tiltDeg: +e.target.value })} /><b>{active.tiltDeg}°</b></div>
          <div className="slider-field">{t("site.grid")} <input type="range" min={-90} max={90} step={1} value={active.gridRotationDeg} onChange={(e) => patchActive({ gridRotationDeg: +e.target.value })} /><b>{active.gridRotationDeg}°</b></div>
          <div className="slider-field">{t("site.setback")} <input type="range" min={0} max={2} step={0.1} value={active.edgeSetbackM} onChange={(e) => patchActive({ edgeSetbackM: +e.target.value })} /><b>{active.edgeSetbackM} m</b></div>
          {/* E42.0: zoom/fit moved ONTO the map (SiteCanvas overlay) — affordances, not toolbar clutter */}
          <span className="sep" />
          <button className="btn btn-sm" onClick={() => patchActive({ geometry: { vertices: [], obstacles: [] } })}>{t("site.clearSurface")}</button>
        </div>
        <SiteCanvas
          lat={lat} lon={lon} basemap={basemap}
          surfaces={surfaces.map((s) => ({ id: s.id, geometry: s.geometry, rotationDeg: s.gridRotationDeg, edgeSetbackM: s.edgeSetbackM, kind: s.kind, tiltDeg: s.tiltDeg, azimuthDeg: s.azimuthDeg, obstacleBufferM: s.obstacleBufferM }))}
          activeSurfaceId={active.id}
          view={view} tool={tool} mode="site"
          onGeometryChange={(sid, g) => setSurfaces((all) => all.map((s) => (s.id === sid ? { ...s, geometry: g } : s)))}
          onViewChange={setView}
        />
        <div className="legend">
          <span>{surfaces.length} {t("site.legendSurfaces")} {active.name} ({active.geometry.vertices.length} {t("site.legendPts")}, {active.geometry.obstacles.length} {t("site.legendObstacles")})</span>
          {/* E59: the legend mirrors the credit of the pixels actually on screen
              (licence text, verbatim — not localized); the canvas renders it too */}
          <span>{basemap && basemap.kind === "wms" ? basemap.attribution : t("site.imagery")}</span>
        </div>
      </div>
      {plzWarning && (
        <div className="msg warn" data-testid="plz-warning">
          ⚠ {t("site.plzMismatch").replace("{typed}", plzWarning.typed).replace("{resolved}", plzWarning.resolved)}
        </div>
      )}
      {note && <div className={`msg ${note.startsWith(t("common.error")) ? "err" : "info"}`}>{note}</div>}
      {/* E54: ambiguous detection asks — candidate outlines are on the imagery above */}
      {candidates && (
        <div className="card" style={{ marginBottom: 10 }} data-testid="building-picker">
          {candidates.map((cd, i) => (
            <div key={cd.buildingId} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6 }}>
              <button
                className={`btn btn-sm ${activeId === `cand-${cd.buildingId}` ? "active" : ""}`}
                onClick={() => { setActiveId(`cand-${cd.buildingId}`); }}
              >
                {t("site.candidate")} {i + 1}
              </button>
              <span className="muted" style={{ fontSize: 12 }}>
                {t("site.candidateMeta", { area: cd.roofAreaM2, planes: cd.planeCount, dist: cd.distanceM })}
              </span>
              <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => void detect("auto", cd.buildingId)}>
                {t("site.pickThis")}
              </button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 10 }}>
        <button className="btn btn-primary" disabled={busy} onClick={() => void save()}>{busy ? <span className="spinner" /> : t("site.save")}</button>
        <button className="btn" onClick={onNext}>{t("site.continueDesign")}</button>
      </div>
      <GeoSourcesCard detail={detail} onReload={onReload} />
      <div>
      </div>
    </div>
  );
}

/* ------------------------------ Design panel ----------------------------- */

type Overrides = Record<string, { removed: Set<string>; moved: Record<string, { dx: number; dy: number }> }>;

function DesignPanel({ detail, catalogue, onReload, onNext }: {
  detail: ProjectDetail;
  catalogue: { modules: CatalogueModule[]; inverters: CatalogueInverter[]; batteries: CatalogueBattery[]; evChargers: CatalogueEvCharger[]; heatPumps: CatalogueHeatPump[]; ems: CatalogueEms[]; meters: CatalogueMeter[]; microinverters: CatalogueMicroinverter[]; optimizers: CatalogueOptimizer[] };
  onReload: () => Promise<void>;
  onNext: () => void;
}): JSX.Element {
  const t = useT();
  const pid = detail.project.id;
  const last = detail.designs[detail.designs.length - 1];
  const [moduleId, setModuleId] = useState(last?.moduleId ?? "mod-jinko-450");
  const [moduleWp, setModuleWp] = useState<number | undefined>(last?.moduleWp ?? undefined);
  const [inverterId, setInverterId] = useState(last?.inverterId ?? "inv-fronius-10");
  const [useIntegrated, setUseIntegrated] = useState(last ? last.inverterId === null : false);
  const [inverterKw, setInverterKw] = useState<number | undefined>(last?.inverterAcKw ?? undefined);
  const [batteryId, setBatteryId] = useState<string | null>(last?.batteryId ?? null);
  const [microinverterId, setMicroinverterId] = useState<string | null>(last?.microinverterId ?? null);
  const [mlpeAdvice, setMlpeAdvice] = useState<MlpeAdvice | null>(null);
  const [mlpeBusy, setMlpeBusy] = useState(false);
  const [optimizerId, setOptimizerId] = useState<string | null>(last?.optimizerId ?? null);
  const [evChargerId, setEvChargerId] = useState<string | null>(last?.evChargerId ?? null);
  const [heatPumpId, setHeatPumpId] = useState<string | null>(last?.heatPumpId ?? null);
  const [emsId, setEmsId] = useState<string | null>(last?.emsId ?? null);
  const [meterId, setMeterId] = useState<string | null>(last?.meterId ?? null);
  const [dispatchStrategy, setDispatchStrategy] = useState<"self_consumption" | "price_optimized">(
    (last?.dispatchStrategy as "self_consumption" | "price_optimized") ?? "self_consumption",
  );
  const [orientation, setOrientation] = useState<"portrait" | "landscape">(last?.layoutOptions.orientation ?? "portrait");
  const [rowGap, setRowGap] = useState(last?.layoutOptions.rowGapM ?? 0);
  const [overrides, setOverrides] = useState<Overrides>(() => {
    const out: Overrides = {};
    const so = last?.surfaceOverrides ?? {};
    for (const [sid, ov] of Object.entries(so)) {
      out[sid] = { removed: new Set(ov.removedModuleKeys ?? []), moved: { ...(ov.movedModules ?? {}) } };
    }
    return out;
  });
  const [activeSid, setActiveSid] = useState<string | null>(detail.surfaces[0]?.id ?? null);
  const [stats, setStats] = useState<Record<string, { count: number; kwp: number }>>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [view, setView] = useState<View>(fitViewFor(detail.surfaces.map((s) => ({ geometry: { vertices: s.boundaryLocal.vertices, obstacles: [] } }))));
  // E59 — per-Bundesland official basemap; null → Esri (degrade, don't break)
  const [basemap, setBasemap] = useState<ClientBasemap | null>(null);
  useEffect(() => {
    api.projects.basemap(detail.project.id).then((r) => setBasemap(r.active)).catch(() => setBasemap(null));
  }, [detail.project.id]);

  const selectedModule = catalogue.modules.find((x) => x.id === moduleId);
  const moduleSpec = useMemo(() => {
    if (!selectedModule) return null;
    const spec = toModuleSpec(selectedModule);
    const v = moduleWp !== undefined ? selectedModule.variants?.find((x) => x.wp === moduleWp) : undefined;
    if (v) {
      spec.wp = v.wp;
      spec.vocV = v.vocV;
      spec.vmppV = v.vmppV;
      spec.imppA = v.imppA;
      spec.iscA = v.iscA;
      spec.efficiencyPct = v.efficiencyPct;
      spec.priceEur = v.priceEur;
    }
    return spec;
  }, [selectedModule, moduleWp]);

  const inverter = catalogue.inverters.find((i) => i.id === inverterId);
  const inverterVariant = inverterKw !== undefined ? inverter?.variants?.find((v) => v.acKw === inverterKw) : undefined;
  const battery = batteryId ? catalogue.batteries.find((b) => b.id === batteryId) : null;
  const integratedAvailable = !!battery?.integratedInverter;
  const integrated = useIntegrated && integratedAvailable;
  const effectiveAcKw = integrated
    ? battery!.integratedInverter!.acKw
    : (inverterVariant?.acKw ?? inverter?.acKw ?? 10);
  const compatible =
    integrated || !battery || battery.acCoupled || (inverter?.compatibleBatteryIds.includes(battery.id) ?? false);

  const totals = Object.values(stats).reduce((a, s) => ({ count: a.count + s.count, kwp: a.kwp + s.kwp }), { count: 0, kwp: 0 });
  const suggestedInverterCount = integrated ? 1 : Math.max(1, Math.ceil((totals.kwp * 0.85) / effectiveAcKw));

  const canvasSurfaces = detail.surfaces.map((s) => ({
    id: s.id,
    geometry: { vertices: s.boundaryLocal.vertices, obstacles: s.obstacles },
    rotationDeg: s.gridRotationDeg ?? 0,
    edgeSetbackM: s.edgeSetbackM,
    kind: s.kind,
    // E63.1: the REAL tilt — the engine keys the E55 correction on it, so a
    // hardcoded 35° here made the toolbar kWp diverge from the simulation
    tiltDeg: s.tiltDeg,
    azimuthDeg: s.azimuthDeg,
    obstacleBufferM: s.obstacleBufferM,
    removedKeys: overrides[s.id]?.removed,
    movedOffsets: overrides[s.id]?.moved,
  }));

  function ov(sid: string) {
    return overrides[sid] ?? { removed: new Set<string>(), moved: {} };
  }

  async function saveDesign(): Promise<void> {
    setBusy(true);
    setNote("");
    try {
      const surfaceOverrides: Record<string, { removedModuleKeys: string[]; movedModules: Record<string, { dx: number; dy: number }> }> = {};
      for (const [sid, o] of Object.entries(overrides)) {
        surfaceOverrides[sid] = { removedModuleKeys: [...o.removed], movedModules: o.moved };
      }
      await api.projects.addDesign(pid, {
        name: `${t("design.variantName")} ${String.fromCharCode(65 + detail.designs.length)}`,
        moduleId,
        inverterId: integrated || microinverterId ? null : inverterId,
        inverterCount: suggestedInverterCount,
        batteryId,
        microinverterId,
        optimizerId: microinverterId ? null : optimizerId,
        evChargerId,
        heatPumpId,
        emsId,
        meterId,
        dispatchStrategy,
        moduleWp,
        inverterAcKw: integrated ? undefined : inverterKw,
        surfaceOverrides,
        layoutOptions: { orientation, moduleGapM: 0.02, rowGapM: rowGap }, // E55: tilt correction unconditional in the engine
        commissioningDate: new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
      });
      await onReload();
      setNote(t("design.designSaved"));
    } catch (e) {
      setNote(`${t("common.error")}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  const movedCount = Object.values(overrides).reduce((n, o) => n + Object.keys(o.moved).length, 0);
  const removedCount = Object.values(overrides).reduce((n, o) => n + o.removed.size, 0);

  return (
    <div>
      <h1>{t("design.title")}</h1>
      <div className="sub">{t("design.hint")}</div>
      {detail.surfaces.length === 0 && <div className="msg err">{t("design.noSurfaces")}</div>}
      {detail.surfaces.length > 0 && moduleSpec && (
        <div className="canvas-wrap">
          <div className="canvas-tools">
            {detail.surfaces.length > 1 && (
              <>
                <label style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>{t("design.edit")}</label>
                <select value={activeSid ?? ""} onChange={(e) => setActiveSid(e.target.value)}>
                  {detail.surfaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <span className="sep" />
              </>
            )}
            <button className={`btn btn-sm ${orientation === "portrait" ? "active" : ""}`} onClick={() => { setOrientation("portrait"); setOverrides({}); }}>{t("design.portrait")}</button>
            <button className={`btn btn-sm ${orientation === "landscape" ? "active" : ""}`} onClick={() => { setOrientation("landscape"); setOverrides({}); }}>{t("design.landscape")}</button>
            {detail.project.projectType !== "RESIDENTIAL_PITCHED" && (
              <div className="slider-field">{t("design.rowGap")} <input type="range" min={0} max={6} step={0.25} value={rowGap} onChange={(e) => { setRowGap(+e.target.value); setOverrides({}); }} /><b>{rowGap} m</b></div>
            )}
            {(removedCount > 0 || movedCount > 0) && (
              <button className="btn btn-sm" onClick={() => setOverrides({})}>{t("design.reset", { removed: removedCount, moved: movedCount })}</button>
            )}
            <span className="sep" />
            <span style={{ fontSize: 12.5 }}>
              <b>{totals.count}</b> {t("design.modules")} · <b>{fmt(totals.kwp, 2)} kWp</b>
              {moduleSpec && <> · {fmt(totals.count * moduleSpec.widthM * moduleSpec.heightM, 1)} m² · {moduleSpec.widthM.toFixed(3)} × {moduleSpec.heightM.toFixed(3)} m</>}
            </span>
            {/* E42.0: zoom moved ONTO the map (SiteCanvas overlay) */}
          </div>
          <SiteCanvas
            lat={detail.site?.lat ?? 48.14} lon={detail.site?.lon ?? 11.58} basemap={basemap}
            surfaces={canvasSurfaces}
            activeSurfaceId={activeSid}
            view={view} tool="module" mode="layout"
            module={moduleSpec}
            layoutOptions={{ orientation, moduleGapM: 0.02, rowGapM: rowGap }}
            onViewChange={setView}
            onToggleModule={(sid, key) =>
              setOverrides((all) => {
                const o = ov(sid);
                const removed = new Set(o.removed);
                if (removed.has(key)) removed.delete(key);
                else removed.add(key);
                return { ...all, [sid]: { ...o, removed } };
              })
            }
            onMoveModule={(sid, key, dx, dy) =>
              setOverrides((all) => {
                const o = ov(sid);
                const moved = { ...o.moved };
                if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) delete moved[key];
                else moved[key] = { dx, dy };
                return { ...all, [sid]: { ...o, moved } };
              })
            }
            onModuleStats={(perSurface) => {
              const changed = JSON.stringify(perSurface) !== JSON.stringify(stats);
              if (changed) setStats(perSurface);
            }}
          />
          <div className="legend">
            {detail.surfaces.map((s) => (
              <span key={s.id}>{s.name}: <b>{stats[s.id]?.count ?? 0}</b> mod · {fmt(stats[s.id]?.kwp ?? 0, 1)} kWp{(s.gridRotationDeg ?? 0) !== 0 ? ` · ↻${s.gridRotationDeg}°` : ""}</span>
            ))}
            <span>{t("design.legendClick")}</span>
          </div>
        </div>
      )}
      <div className="comp-cols">
        <div>
          <h3 style={{ marginBottom: 8 }}>{t("design.pvModule")}</h3>
          <div className="comp-list">
            {catalogue.modules.map((m) => (
              <div key={m.id} className={`ccard ${m.id === moduleId ? "sel" : ""}`} onClick={() => { if (m.id !== moduleId) { setModuleId(m.id); setModuleWp(undefined); } }}>
                <span className="pr">{(m.id === moduleId && moduleSpec ? moduleSpec.priceEur : m.listPriceEur)} €</span>
                <div className="nm">{m.manufacturer} {m.model.split(" ").slice(0, 3).join(" ")}</div>
                <div className="meta">
                  {m.id === moduleId && moduleSpec ? moduleSpec.wp : m.wp} Wp · {(m.id === moduleId && moduleSpec ? moduleSpec.efficiencyPct : m.efficiencyPct).toFixed(1)}% · {m.cellTechnology}
                  <br />{m.widthM.toFixed(3)} × {m.heightM.toFixed(3)} m · {(m.widthM * m.heightM).toFixed(2)} m² · {m.warrantyProductYears}/{m.warrantyPerformanceYears} yr
                </div>
                {m.id === moduleId && m.variants && m.variants.length > 1 && (
                  <div style={{ marginTop: 7 }} onClick={(e) => e.stopPropagation()}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4, marginRight: 6 }}>{t("design.powerClass")}</label>
                    <select value={moduleWp ?? m.wp} onChange={(e) => setModuleWp(+e.target.value)} style={{ padding: "3px 6px", fontSize: 12 }}>
                      {m.variants.map((v) => (
                        <option key={v.wp} value={v.wp}>{v.wp} Wp — {v.priceEur} € (Voc {v.vocV.toFixed(1)} V)</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 style={{ marginBottom: 8 }}>{t("design.inverter")}</h3>
          <div className="comp-list">
            {integratedAvailable && (
              <div className={`ccard ${integrated ? "sel" : ""}`} onClick={() => setUseIntegrated(true)}>
                <span className="pr">0 €</span>
                <div className="nm">{t("design.integratedIn")} {battery!.manufacturer} {battery!.model}</div>
                <div className="meta">{battery!.integratedInverter!.acKw} {t("design.integratedMeta", { n: battery!.integratedInverter!.mppt.length })}</div>
              </div>
            )}
            {catalogue.inverters.map((i) => (
              <div key={i.id} className={`ccard ${!integrated && i.id === inverterId ? "sel" : ""}`} onClick={() => { setUseIntegrated(false); if (i.id !== inverterId) { setInverterId(i.id); setInverterKw(undefined); } }}>
                <span className="pr">{fmt(i.id === inverterId && inverterVariant ? inverterVariant.priceEur : i.listPriceEur)} €</span>
                <div className="nm">{i.manufacturer} {i.id === inverterId && inverterVariant ? inverterVariant.label : i.model}</div>
                <div className="meta">{i.id === inverterId ? effectiveAcKw : i.acKw} kW AC · {i.hybrid ? t("design.hybrid") : t("design.string")} · {i.class}</div>
                {i.id === inverterId && i.variants && i.variants.length > 1 && (
                  <div style={{ marginTop: 7 }} onClick={(e) => e.stopPropagation()}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4, marginRight: 6 }}>{t("design.powerClass")}</label>
                    <select value={inverterKw ?? i.acKw} onChange={(e) => setInverterKw(+e.target.value)} style={{ padding: "3px 6px", fontSize: 12 }}>
                      {i.variants.map((v) => (
                        <option key={v.acKw} value={v.acKw}>{v.acKw} kW — {v.label} ({fmt(v.priceEur)} €)</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>
          {totals.kwp > 0 && <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>{t("design.suggested", { n: suggestedInverterCount, kwp: fmt(totals.kwp, 1), ratio: fmt(totals.kwp / (effectiveAcKw * suggestedInverterCount), 2) })}</div>}
        </div>
        <div>
          <h3 style={{ marginBottom: 8 }}>{t("design.batteryOperation")}</h3>
          <div className="comp-list">
            <div className={`ccard ${batteryId === null ? "sel" : ""}`} onClick={() => { setBatteryId(null); setUseIntegrated(false); }}>
              <div className="nm">{t("design.noBattery")}</div>
              <div className="meta">{t("design.noBatteryMeta")}</div>
            </div>
            {catalogue.batteries.map((b) => (
              <div key={b.id} className={`ccard ${b.id === batteryId ? "sel" : ""}`} onClick={() => { setBatteryId(b.id); if (b.integratedInverter) setUseIntegrated(true); else setUseIntegrated(false); }}>
                <span className="pr">{fmt(b.listPriceEur)} €</span>
                <div className="nm">{b.manufacturer} {b.model}</div>
                <div className="meta">{b.usableKwh} kWh · {b.integratedInverter ? t("design.hybridIncl", { kw: b.integratedInverter.acKw }) : b.acCoupled ? t("design.acCoupled") : "DC"} · {b.warrantyYears} {t("proj.yearsShort")}</div>
              </div>
            ))}
          </div>
          <div className="field" style={{ marginTop: 8 }}>
            <label>{t("design.dispatch")}</label>
            <select value={dispatchStrategy} onChange={(e) => setDispatchStrategy(e.target.value as "self_consumption" | "price_optimized")}>
              <option value="self_consumption">{t("design.dispatch.self")}</option>
              <option value="price_optimized">{t("design.dispatch.price")}</option>
            </select>
          </div>
        </div>
      </div>
      <div className="card" style={{ marginTop: 14 }}>
        <h3 style={{ marginBottom: 4 }}>{t("design.mlpeTitle")}</h3>
        <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
          {t("design.mlpeHint")}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div className="field">
            <label>{t("design.micro")}</label>
            <select
              value={microinverterId ?? ""}
              onChange={(e) => {
                setMicroinverterId(e.target.value || null);
                if (e.target.value) setOptimizerId(null);
              }}
            >
              <option value="">{t("design.microNone")}</option>
              {catalogue.microinverters.map((x) => (
                <option key={x.id} value={x.id}>{x.manufacturer} {x.model} · {x.unitAcVa} VA · {fmt(x.listPriceEur)} {t("design.perUnit")}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>{t("design.optimizer")}</label>
            <select value={optimizerId ?? ""} disabled={!!microinverterId} onChange={(e) => setOptimizerId(e.target.value || null)}>
              <option value="">{t("design.optimizerNone")}</option>
              {catalogue.optimizers.map((x) => (
                <option key={x.id} value={x.id}>{x.manufacturer} {x.model}{x.requiresInverterBrand ? ` · ${t("design.requires")} ${x.requiresInverterBrand}` : x.compatibleInverterBrands.includes("*") ? ` · ${t("design.anyInverter")}` : ` · ${x.compatibleInverterBrands.join("/")}`} · {fmt(x.listPriceEur)} {t("design.perModule")}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          <button
            className="btn btn-o btn-sm"
            disabled={mlpeBusy || detail.designs.length === 0}
            onClick={() => {
              const d = detail.designs[detail.designs.length - 1]!;
              setMlpeBusy(true);
              api.projects
                .mlpeAdvice(pid, d.id)
                .then(setMlpeAdvice)
                .catch(() => setMlpeAdvice(null))
                .finally(() => setMlpeBusy(false));
            }}
          >
            {mlpeBusy ? <span className="spinner" /> : t("design.mlpeAsk")}
          </button>
          {detail.designs.length === 0 && <span className="muted" style={{ fontSize: 11.5, marginLeft: 8 }}>{t("design.saveFirst")}</span>}
        </div>
        {mlpeAdvice && (
          <div style={{ marginTop: 10 }}>
            <div className="msg info">
              {t("design.mlpeSummary", { shade: mlpeAdvice.shadeDispersionPct, mismatch: mlpeAdvice.seriesMismatchLossPct, arch: mlpeAdvice.baseline.architecture, kwh: fmt(mlpeAdvice.baseline.annualAcKwh), npv: eur(mlpeAdvice.baseline.npvEur) })}
            </div>
            <table className="tbl" style={{ marginTop: 8 }}>
              <thead><tr><th>{t("design.col.option")}</th><th className="r">{t("design.col.gain")}</th><th className="r">{t("design.col.addedCost")}</th><th className="r">{t("design.col.npv")}</th><th className="r">{t("design.col.payback")}</th><th>{t("design.col.verdict")}</th></tr></thead>
              <tbody>
                {mlpeAdvice.options.map((o) => (
                  <tr key={o.componentId}>
                    <td><b>{o.label}</b><br /><span className="muted" style={{ fontSize: 10.5 }}>{o.reasons[0]}</span></td>
                    <td className="r">{o.gainKwhYear} kWh (+{o.gainPct} %)</td>
                    <td className="r">{eur(o.addedInvestmentEur)}</td>
                    <td className="r" style={{ color: o.npvDeltaEur >= 0 ? "var(--pos)" : "var(--neg)" }}>{o.npvDeltaEur >= 0 ? "+" : ""}{eur(o.npvDeltaEur)}</td>
                    <td className="r">{o.paybackDeltaYears != null ? `${o.paybackDeltaYears >= 0 ? "+" : ""}${o.paybackDeltaYears} ${t("proj.yearsShort")}` : "—"}</td>
                    <td>
                      <span className={`chip ${o.verdict === "recommended" || o.verdict === "required" ? "ok" : o.verdict === "incompatible" ? "bad" : ""}`}>
                        {o.verdict === "required" ? t("design.requiredArch") : o.verdict.replace("_", " ")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className={`msg ${mlpeAdvice.recommendation.choice === "none" ? "info" : "ok"}`} style={{ marginTop: 8 }}>
              {mlpeAdvice.recommendation.explanation}
            </div>
            {mlpeAdvice.recommendation.componentId && (
              <button
                className="btn btn-sm"
                onClick={() => {
                  if (mlpeAdvice.recommendation.choice === "optimizer") {
                    setOptimizerId(mlpeAdvice.recommendation.componentId);
                    setMicroinverterId(null);
                  } else {
                    setMicroinverterId(mlpeAdvice.recommendation.componentId);
                    setOptimizerId(null);
                  }
                }}
              >
                {t("design.applyRecommendation")}
              </button>
            )}
          </div>
        )}
        {(() => {
          const opt = catalogue.optimizers.find((x) => x.id === optimizerId);
          const invBrand = catalogue.inverters.find((x) => x.id === inverterId)?.manufacturer;
          const bat = catalogue.batteries.find((x) => x.id === batteryId);
          const msgs: { cls: string; text: string }[] = [];
          if (microinverterId && bat && !bat.acCoupled) msgs.push({ cls: "err", text: t("design.adv.dcCoupled", { model: bat.model }) });
          if (opt?.requiresInverterBrand && invBrand !== opt.requiresInverterBrand && !microinverterId) msgs.push({ cls: "err", text: t("design.adv.optBrand", { model: opt.model, brand: opt.requiresInverterBrand }) });
          if (!microinverterId && !optimizerId && invBrand === "SolarEdge") msgs.push({ cls: "warn", text: t("design.adv.solaredge") });
          return msgs.map((m, i) => <div key={i} className={`msg ${m.cls}`} style={{ marginTop: 8 }}>{m.text}</div>);
        })()}
      </div>
      <div className="card" style={{ marginTop: 14 }}>
        <h3 style={{ marginBottom: 4 }}>{t("design.ecoTitle")}</h3>
        <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
          {t("design.ecoHint")}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div className="field">
            <label>{t("design.evCharger")}</label>
            <select value={evChargerId ?? ""} onChange={(e) => setEvChargerId(e.target.value || null)}>
              <option value="">{t("design.none")}</option>
              {catalogue.evChargers.map((x) => (
                <option key={x.id} value={x.id}>{x.manufacturer} {x.model} · {x.acKw} kW{x.pvSurplusCapable ? ` · ${t("design.pvSurplus")}` : ""} · {fmt(x.listPriceEur)} €</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>{t("design.heatPump")}</label>
            <select value={heatPumpId ?? ""} onChange={(e) => setHeatPumpId(e.target.value || null)}>
              <option value="">{t("design.none")}</option>
              {catalogue.heatPumps.map((x) => (
                <option key={x.id} value={x.id}>{x.manufacturer} {x.model} · {x.thermalKw} {t("design.thermalKw")} · SCOP {x.scopW35}{x.sgReady ? " · SG-Ready" : ""} · {fmt(x.listPriceEur)} €</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>{t("design.ems")}</label>
            <select value={emsId ?? ""} onChange={(e) => setEmsId(e.target.value || null)}>
              <option value="">{t("design.none")}</option>
              {catalogue.ems.map((x) => (
                <option key={x.id} value={x.id}>{x.manufacturer} {x.model}{x.compatibleInverterBrands.includes("*") ? ` · ${t("design.allBrands")}` : ` · ${x.compatibleInverterBrands.join("/")}`}{x.listPriceEur > 0 ? ` · ${fmt(x.listPriceEur)} €` : ` · ${t("design.included")}`}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>{t("design.meter")}</label>
            <select value={meterId ?? ""} onChange={(e) => setMeterId(e.target.value || null)}>
              <option value="">{t("design.meterGeneric")}</option>
              {catalogue.meters.map((x) => (
                <option key={x.id} value={x.id}>{x.manufacturer} {x.model} · {x.maxCurrentA} A · {x.protocol} · {fmt(x.listPriceEur)} €</option>
              ))}
            </select>
          </div>
        </div>
        {(() => {
          const emsSel = catalogue.ems.find((x) => x.id === emsId);
          const chSel = catalogue.evChargers.find((x) => x.id === evChargerId);
          const hpSel = catalogue.heatPumps.find((x) => x.id === heatPumpId);
          const invBrand = integrated ? "Tesla" : catalogue.inverters.find((x) => x.id === inverterId)?.manufacturer;
          const msgs: { cls: string; text: string }[] = [];
          if (emsSel && !emsSel.compatibleInverterBrands.includes("*") && invBrand && !emsSel.compatibleInverterBrands.includes(invBrand)) {
            msgs.push({ cls: "err", text: t("design.adv.emsBrand", { model: emsSel.model, brands: emsSel.compatibleInverterBrands.join("/"), brand: invBrand }) });
          }
          if (chSel?.pvSurplusCapable && !emsSel) msgs.push({ cls: "info", text: t("design.adv.surplusCharging", { model: chSel.model }) });
          if (hpSel?.sgReady && !emsSel) msgs.push({ cls: "info", text: t("design.adv.sgReady", { model: hpSel.model }) });
          if (emsSel && (chSel?.pvSurplusCapable || hpSel?.sgReady) && (emsSel.compatibleInverterBrands.includes("*") || (invBrand && emsSel.compatibleInverterBrands.includes(invBrand)))) {
            msgs.push({ cls: "ok", text: t("design.adv.surplusActive") });
          }
          return msgs.map((m, i) => <div key={i} className={`msg ${m.cls}`} style={{ marginTop: 8 }}>{m.text}</div>);
        })()}
      </div>
      <div style={{ marginTop: 14 }}>
        {!compatible && battery && inverter && (
          <div className="msg err">{t("design.adv.notReleased", { battery: `${battery.manufacturer} ${battery.model}`, inverter: `${inverter.manufacturer} ${inverter.model}` })}</div>
        )}
        {compatible && battery && (
          <div className="msg ok">
            {integrated
              ? t("design.adv.containsOwn", { model: battery.model, kw: battery.integratedInverter!.acKw })
              : battery.acCoupled
                ? t("design.adv.acWorksAny", { model: battery.model })
                : t("design.adv.compatibleWith", { model: battery.model, inverter: inverter?.model ?? "" })}
          </div>
        )}
        {note && <div className={`msg ${note.startsWith(t("common.error")) ? "err" : "ok"}`}>{note}</div>}
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-primary" disabled={busy || !compatible || detail.surfaces.length === 0} onClick={() => void saveDesign()}>
            {busy ? <span className="spinner" /> : detail.designs.length ? t("design.saveVariant") : t("design.saveDesign")}
          </button>
          <button className="btn" onClick={onNext}>{t("design.continueSim")}</button>
        </div>
        {detail.designs.length > 0 && (
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            {t("design.versions")}{" "}
            {detail.designs.map((d) => (
              <span key={d.id} style={{ marginRight: 10 }}>
                v{d.version} {d.name.length > 34 ? `${d.name.slice(0, 34)}…` : d.name}{" "}
                <span className={`chip ${d.origin === "ai" ? "info" : ""}`}>{d.origin === "ai" ? "✨ AI" : t("design.manual")}</span>
              </span>
            ))}
          </div>
        )}
      </div>
      <AutoDesignCard detail={detail} onReload={onReload} />
    </div>
  );
}

/* --------------------------- AI Auto-Designer ----------------------------- */

function AutoDesignCard({ detail, onReload }: { detail: ProjectDetail; onReload: () => Promise<void> }): JSX.Element {
  const t = useT();
  const [sweep, setSweep] = useState<AutoDesignResponse | null>(null);
  const [objective, setObjective] = useState("npv");
  const [budget, setBudget] = useState("");
  const [maxPayback, setMaxPayback] = useState("");
  const [baseline, setBaseline] = useState<{ name: string; npvEur: number; paybackYears: number | null; investmentEur: number; selfSufficiencyPct: number; dcAcRatio: number; annualAcKwh: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [accepting, setAccepting] = useState<number | null>(null);
  const [note, setNote] = useState("");

  const manual = [...detail.designs].reverse().find((d) => (d.origin ?? "manual") === "manual");

  async function runSweep(): Promise<void> {
    setBusy(true);
    setNote("");
    try {
      const [sw, base] = await Promise.all([
        api.projects.autodesign(detail.project.id, {
          apply: false,
          objective,
          maxBudgetEur: budget ? +budget : undefined,
          maxPaybackYears: maxPayback ? +maxPayback : undefined,
          priceSensitivityPct: [-20, 0, 40],
        }),
        manual
          ? api.projects.simulate(detail.project.id, manual.id).then((r) => ({
              name: manual.name,
              npvEur: r.result.finance.npvEur,
              paybackYears: r.result.finance.paybackYears,
              investmentEur: r.result.finance.investmentEur,
              selfSufficiencyPct: r.result.balance.selfSufficiencyRate * 100,
              dcAcRatio: r.result.strings.dcAcRatio,
              annualAcKwh: r.result.energy.annualAcKwh,
            }))
          : Promise.resolve(null),
      ]);
      setSweep(sw);
      setBaseline(base);
    } catch (e) {
      setNote(`${t("common.error")}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function accept(c: DesignCandidate): Promise<void> {
    setAccepting(c.rank);
    setNote("");
    try {
      const res = await api.projects.autodesign(detail.project.id, { apply: true, applyRank: c.rank });
      await onReload();
      setNote(res.createdDesign ? t("ai.createdVariant", { name: res.createdDesign.name }) : t("ai.accepted"));
    } catch (e) {
      setNote(`${t("common.error")}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setAccepting(null);
    }
  }

  const nf = (v: number): string => Math.round(v).toLocaleString("de-DE");

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ marginBottom: 4 }}>{t("ai.title")}</h3>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 10 }}>
        {t("ai.hint")}
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap", marginBottom: 10 }}>
        <div className="field">
          <label>{t("ai.objective")}</label>
          <select value={objective} onChange={(e) => setObjective(e.target.value)}>
            <option value="npv">{t("ai.obj.npv")}</option>
            <option value="payback">{t("ai.obj.payback")}</option>
            <option value="autarky">{t("ai.obj.autarky")}</option>
            <option value="cost">{t("ai.obj.cost")}</option>
            <option value="production">{t("ai.obj.production")}</option>
          </select>
        </div>
        <div className="field"><label>{t("ai.budget")}</label><input type="number" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder={t("ai.budgetPlaceholder")} style={{ width: 120 }} /></div>
        <div className="field"><label>{t("ai.maxPayback")}</label><input type="number" value={maxPayback} onChange={(e) => setMaxPayback(e.target.value)} placeholder={t("ai.maxPaybackPlaceholder")} style={{ width: 110 }} /></div>
        <button className="btn btn-primary" disabled={busy || detail.surfaces.length === 0} onClick={() => void runSweep()}>
          {busy ? <span className="spinner" /> : sweep ? t("ai.rerun") : t("ai.run")}
        </button>
      </div>
      {note && <div className={`msg ${note.startsWith(t("common.error")) ? "err" : "ok"}`} style={{ marginTop: 10 }}>{note}</div>}
      {sweep && (
        <div style={{ marginTop: 12 }}>
          <div className="muted" style={{ fontSize: 11.5, marginBottom: 6 }}>
            {t("ai.evaluated", { n: sweep.evaluated, module: `${sweep.module.name}${sweep.module.wp ? ` (${sweep.module.wp} Wp)` : ""}`, weather: sweep.weatherSource === "pvgis_sarah3" ? "PVGIS SARAH3" : t("ai.synthetic") })}
          </div>
          <table className="tbl">
            <thead>
              <tr><th>#</th><th>{t("ai.col.system")}</th><th className="r">DC/AC</th><th className="r">{t("ai.col.autarky")}</th><th className="r">{t("ai.col.invest")}</th><th className="r">{t("ai.col.npv")}</th><th className="r">{t("ai.col.payback")}</th><th /></tr>
            </thead>
            <tbody>
              {baseline && (
                <tr style={{ background: "var(--row-highlight)" }}>
                  <td><span className="chip">{t("ai.you")}</span></td>
                  <td><b>{baseline.name}</b><br /><span className="muted" style={{ fontSize: 11 }}>{t("ai.yourBaseline")}</span></td>
                  <td className="r">{baseline.dcAcRatio.toFixed(2)}</td>
                  <td className="r">{baseline.selfSufficiencyPct.toFixed(0)} %</td>
                  <td className="r">{nf(baseline.investmentEur)} €</td>
                  <td className="r"><b>{nf(baseline.npvEur)} €</b></td>
                  <td className="r">{baseline.paybackYears != null ? `${baseline.paybackYears.toFixed(1)} ${t("proj.yearsShort")}` : "—"}</td>
                  <td />
                </tr>
              )}
              {sweep.candidates.map((c) => {
                const beatsBaseline = baseline ? c.npvEur > baseline.npvEur : false;
                return (
                  <tr key={c.rank} style={c.rank === 1 ? { background: "var(--row-success)" } : undefined}>
                    <td>{c.rank}</td>
                    <td>
                      <b>{c.label}</b>
                      {c.rank === 1 && <span className="chip ok" style={{ marginLeft: 6 }}>{t("ai.best")}</span>}
                      {!c.stringsValid && <span className="chip bad" style={{ marginLeft: 6 }}>{t("ai.stringsInvalid")}</span>}
                    {!c.meetsConstraints && <span className="chip warn" style={{ marginLeft: 6 }}>{t("ai.outsideConstraints")}</span>}
                      <br /><span className="muted" style={{ fontSize: 11 }}>{c.rationale}</span>
                    </td>
                    <td className="r">{c.dcAcRatio.toFixed(2)}</td>
                    <td className="r">{c.selfSufficiencyPct.toFixed(0)} %</td>
                    <td className="r">{nf(c.investmentEur)} €</td>
                    <td className="r"><b style={beatsBaseline ? { color: "var(--pos)" } : undefined}>{nf(c.npvEur)} €</b></td>
                    <td className="r">{c.paybackYears != null ? `${c.paybackYears.toFixed(1)} ${t("proj.yearsShort")}` : "—"}</td>
                    <td className="r">
                      <button className="btn btn-sm" disabled={accepting !== null || !c.stringsValid} onClick={() => void accept(c)}>
                        {accepting === c.rank ? <span className="spinner" /> : t("ai.accept")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {sweep.alternatives.length > 1 && (
            <div className="card" style={{ marginTop: 12 }}>
              <h3 style={{ marginBottom: 6 }}>{t("ai.alternatives")}</h3>
              {sweep.alternatives.map((a) => (
                <div key={a.rank} style={{ marginBottom: 6 }}>
                  <span className={`chip ${a.title === "Empfehlung" ? "ok" : "info"}`}>#{a.rank} {a.title}</span>{" "}
                  <span style={{ fontSize: 12.5 }}>{a.tradeOff}</span>
                </div>
              ))}
            </div>
          )}
          {sweep.sensitivity.length > 0 && (
            <div className="card" style={{ marginTop: 12 }}>
              <h3 style={{ marginBottom: 6 }}>{t("ai.sensitivity")}</h3>
              <table className="tbl">
                <thead><tr><th>{t("ai.col.scenario")}</th><th className="r">{t("ai.col.npv")}</th><th className="r">{t("ai.col.payback")}</th><th className="r">{t("ai.col.benefit1")}</th></tr></thead>
                <tbody>
                  {sweep.sensitivity.map((sp) => (
                    <tr key={sp.electricityPriceChangePct} style={sp.electricityPriceChangePct === 0 ? { background: "var(--row-neutral)" } : undefined}>
                      <td>{sp.electricityPriceChangePct === 0 ? t("ai.today") : `${sp.electricityPriceChangePct > 0 ? "+" : ""}${sp.electricityPriceChangePct} %`}</td>
                      <td className="r"><b>{nf(sp.npvEur)} €</b></td>
                      <td className="r">{sp.paybackYears != null ? `${sp.paybackYears.toFixed(1)} ${t("proj.yearsShort")}` : "—"}</td>
                      <td className="r">{nf(sp.annualBenefitYear1Eur)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* --------------------------- Accessories panel ---------------------------- */

const PKG_CATEGORIES: { id: string; labelKey: MessageKey; icon: string }[] = [
  { id: "mounting", labelKey: "pkg.cat.mounting", icon: "🔩" },
  { id: "dc", labelKey: "pkg.cat.dc", icon: "⚡" },
  { id: "ac", labelKey: "pkg.cat.ac", icon: "🔌" },
  { id: "monitoring", labelKey: "pkg.cat.monitoring", icon: "📡" },
];

function PackagePanel({ detail, onReload, onNext }: { detail: ProjectDetail; onReload: () => Promise<void>; onNext: () => void }): JSX.Element {
  const t = useT();
  const design = detail.designs[detail.designs.length - 1];
  const [pkg, setPkg] = useState<InstallationPackage | null>(null);
  const [drafts, setDrafts] = useState<Record<string, number>>({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function load(): Promise<void> {
    if (!design) return;
    try {
      setPkg(await api.projects.designPackage(detail.project.id, design.id));
      setDrafts({});
      setErr("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }
  useEffect(() => {
    void load();
  }, [design?.id]);

  function setQty(sku: string, qty: number): void {
    setDrafts((d) => ({ ...d, [sku]: Math.max(0, qty) }));
  }

  async function save(): Promise<void> {
    if (!design) return;
    setBusy(true);
    try {
      const merged: Record<string, number> = { ...((design.accessoryOverrides as Record<string, number> | null) ?? {}), ...drafts };
      await api.projects.updateDesign(detail.project.id, design.id, { accessoryOverrides: merged });
      await onReload();
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function resetAll(): Promise<void> {
    if (!design) return;
    setBusy(true);
    try {
      await api.projects.updateDesign(detail.project.id, design.id, { accessoryOverrides: null });
      await onReload();
      await load();
    } finally {
      setBusy(false);
    }
  }

  if (!design) {
    return (
      <div>
        <h1>{t("proj.step.package")}</h1>
        <div className="msg err">{t("pkg.noDesign")}</div>
      </div>
    );
  }

  const qtyOf = (i: { sku: string; qty: number }) => drafts[i.sku] ?? i.qty;
  const dirty = Object.keys(drafts).length > 0;

  return (
    <div>
      <h1>{t("pkg.title")}</h1>
      <div className="sub">
        {t("pkg.hint", { info: pkg ? `${pkg.moduleCount} ${t("design.modules")}, ${fmt(pkg.dcKwp, 1)} kWp` : "…" })}
      </div>
      {err && <div className="msg err">{err}</div>}
      {!pkg ? (
        <span className="spinner" />
      ) : (
        <>
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 8 }}>{t("pkg.mainHardware")}</h3>
            <table className="tbl">
              <tbody>
                {pkg.hardware.map((h, i) => (
                  <tr key={i}>
                    <td><b>{h.name}</b><br /><span className="muted" style={{ fontSize: 11 }}>{h.detail}</span></td>
                    <td className="r">{h.qty} ×</td>
                    <td className="r">{fmt(h.unitPriceEur)} €</td>
                    <td className="r"><b>{eur(h.totalEur)}</b></td>
                  </tr>
                ))}
                <tr><td colSpan={3}><b>{t("pkg.hardwareTotal")}</b></td><td className="r"><b>{eur(pkg.hardwareTotalEur)}</b></td></tr>
              </tbody>
            </table>
          </div>

          {PKG_CATEGORIES.map((cat) => {
            const items = pkg.accessories.items.filter((i) => i.category === cat.id);
            if (items.length === 0) return null;
            return (
              <div className="card" key={cat.id} style={{ marginBottom: 14 }}>
                <h3 style={{ marginBottom: 8 }}>{cat.icon} {t(cat.labelKey)} <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>· {eur(pkg.accessories.byCategory[cat.id] ?? 0)}</span></h3>
                <table className="tbl">
                  <thead><tr><th>{t("pkg.col.item")}</th><th className="r" style={{ width: 130 }}>{t("pkg.col.qty")}</th><th className="r">{t("pkg.col.unit")}</th><th className="r">{t("pkg.col.total")}</th></tr></thead>
                  <tbody>
                    {items.map((i) => (
                      <tr key={i.sku} style={qtyOf(i) === 0 ? { opacity: 0.45 } : undefined}>
                        <td>
                          <b>{i.name}</b>{i.optional && <span className="chip" style={{ marginLeft: 6 }}>{t("pkg.optional")}</span>}
                          <br /><span className="muted" style={{ fontSize: 11 }}>{i.rule}</span>
                        </td>
                        <td className="r" style={{ whiteSpace: "nowrap" }}>
                          <button className="btn btn-sm" onClick={() => setQty(i.sku, qtyOf(i) - 1)}>−</button>{" "}
                          <input
                            type="number"
                            value={qtyOf(i)}
                            min={0}
                            onChange={(e) => setQty(i.sku, +e.target.value)}
                            style={{ width: 58, textAlign: "right", padding: "4px 6px" }}
                          />{" "}
                          <button className="btn btn-sm" onClick={() => setQty(i.sku, qtyOf(i) + 1)}>＋</button>
                          <span className="muted" style={{ fontSize: 11, marginLeft: 4 }}>{i.unit}</span>
                        </td>
                        <td className="r">{fmt(i.unitPriceEur, 2)}</td>
                        <td className="r">{eur(qtyOf(i) * i.unitPriceEur)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}

          {pkg.accessories.notes.map((n, i) => (
            <div className="msg info" key={i}>{n}</div>
          ))}

          <div className="tiles">
            <div className="tile"><div className="v">{eur(pkg.hardwareTotalEur)}</div><div className="l">{t("pkg.tileHardware")}</div></div>
            <div className="tile"><div className="v">{eur(pkg.accessories.totalEur)}</div><div className="l">{t("pkg.tileAccessories")}</div></div>
            <div className="tile hl"><div className="v">{eur(pkg.grandTotalEur)}</div><div className="l">{t("pkg.tileTotal")}</div></div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-primary" disabled={busy || !dirty} onClick={() => void save()}>
              {busy ? <span className="spinner" /> : dirty ? t("pkg.saveChanges", { n: Object.keys(drafts).length }) : t("pkg.saved")}
            </button>
            <button className="btn" disabled={busy} onClick={() => void resetAll()}>{t("pkg.autoQty")}</button>
            <button className="btn" onClick={onNext}>{t("pkg.continueSim")}</button>
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
            {t("pkg.priceHint")}
          </div>
        </>
      )}
    </div>
  );
}

/* ----------------------------- Results panel ----------------------------- */

function ResultsPanel({ detail, sim, setSim, setError, gate, onNext }: {
  detail: ProjectDetail;
  sim: SimulationResponse | null;
  setSim: (s: SimulationResponse) => void;
  setError: (e: string) => void;
  gate: ExportGate | null;
  onNext: () => void;
}): JSX.Element {
  const t = useT();
  const dl = makeDl(setError); // E47: download failures surface here, never swallowed
  const remedy = gateRemedy(gate, t);
  const [busy, setBusy] = useState(false);
  const [source, setSource] = useState("pvgis_sarah3");
  const [provider, setProvider] = useState("generic");
  const design = detail.designs[detail.designs.length - 1];

  async function run(): Promise<void> {
    if (!design) return;
    setBusy(true);
    setError("");
    try {
      setSim(await api.projects.simulate(detail.project.id, design.id, { weatherSource: source, tariffProvider: provider }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const r = sim?.result;
  return (
    <div>
      <h1>{t("sim.title")}</h1>
      <div className="sub">{t("sim.hint")}</div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16 }}>
        <select value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="pvgis_sarah3">{t("sim.weather.pvgis")}</option>
          <option value="synthetic_climatology">{t("sim.weather.synthetic")}</option>
        </select>
        <select value={provider} onChange={(e) => setProvider(e.target.value)} title={t("sim.tariffTitle")}>
          <option value="generic">{t("sim.tariff.generic")}</option>
          <option value="octopus">dynamicOctopus (15-min)</option>
          <option value="tibber">Tibber</option>
          <option value="awattar">aWATTar HOURLY</option>
        </select>
        <button className="btn btn-primary" disabled={busy || !design} onClick={() => void run()}>
          {busy ? <><span className="spinner" /> {t("sim.running")}</> : t("sim.run")}
        </button>
        {!design && <span className="muted">{t("sim.createDesignFirst")}</span>}
        {sim && <span className="chip info">{sim.weatherSource}</span>}
      </div>
      {r && (
        <>
          <div className="tiles">
            <div className="tile hl"><div className="v">{fmt(r.energy.annualAcKwh)} kWh</div><div className="l">{t("sim.annualProduction")}</div></div>
            <div className="tile"><div className="v">{fmt(r.energy.specificYieldKwhPerKwp)}</div><div className="l">kWh / kWp</div></div>
            <div className="tile"><div className="v">{fmt(r.energy.performanceRatio * 100, 1)}%</div><div className="l">{t("sim.performanceRatio")}</div></div>
            <div className="tile"><div className="v">{fmt(r.balance.selfSufficiencyRate * 100)}%</div><div className="l">{t("sim.selfSufficiency")}</div></div>
            <div className="tile"><div className="v">{fmt(r.balance.selfConsumedKwh)} kWh</div><div className="l">{t("sim.selfConsumed")}</div></div>
            <div className="tile"><div className="v">{fmt(r.balance.gridFeedInKwh)} kWh</div><div className="l">{t("sim.gridFeedIn")}</div></div>
          </div>
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 8 }}>{t("sim.monthlyProduction")}</h3>
            <BarChart labels={MONTHS} values={r.energy.monthlyAcKwh} unit="kWh" />
          </div>
          <div className="tiles">
            <div className="tile hl"><div className="v">{r.finance.paybackYears ? `${fmt(r.finance.paybackYears, 1)} ${t("proj.yearsShort")}` : "–"}</div><div className="l">{t("sim.payback")}</div></div>
            <div className="tile"><div className="v">{eur(r.finance.investmentEur)}</div><div className="l">{t("sim.investment")} ({fmt(r.cost.eurPerKwp)} €/kWp{r.cost.vatRate === 0 ? t("sim.vat0") : ""})</div></div>
            <div className="tile"><div className="v">{eur(r.finance.annualBenefitYear1Eur)}</div><div className="l">{t("sim.benefitYear1")}</div></div>
            <div className="tile"><div className="v">{eur(r.finance.npvEur)}</div><div className="l">{t("sim.npv25")}</div></div>
            <div className="tile"><div className="v">{r.finance.irrPct ? `${fmt(r.finance.irrPct, 1)}%` : "–"}</div><div className="l">{t("sim.irr")}</div></div>
            <div className="tile"><div className="v">{fmt(r.finance.lcoeEurPerKwh * 100, 1)} ct</div><div className="l">{t("sim.lcoe")}</div></div>
          </div>
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 8 }}>{t("sim.cashflow")}</h3>
            <CashflowChart cashflow={r.finance.cashflow} investment={r.finance.investmentEur} />
          </div>
          {r.dynamicTariff && (
            <div className="card" style={{ marginBottom: 14 }}>
              <h3 style={{ marginBottom: 8 }}>{t("sim.dynTariff", { provider: r.dynamicTariff.provider })}</h3>
              <div className="tiles">
                <div className="tile"><div className="v">{fmt(r.dynamicTariff.spotAvgCtPerKwh, 1)} ct</div><div className="l">{t("sim.avgSpot")}</div></div>
                <div className="tile"><div className="v">{fmt(r.dynamicTariff.negativeHourSharePct, 1)}%</div><div className="l">{t("sim.negHours")}</div></div>
                <div className="tile"><div className="v">{fmt(r.dynamicTariff.feedInNegativeSharePct, 1)}%</div><div className="l">{t("sim.s51Share")}</div></div>
                <div className="tile"><div className="v">{eur(r.dynamicTariff.importCostDynamicEur)}</div><div className="l">{t("sim.importDynamic", { flat: eur(r.dynamicTariff.importCostFlatEur) })}</div></div>
                {r.dynamicTariff.arbitrageGainEur > 0 && (
                  <div className="tile"><div className="v">{eur(r.dynamicTariff.arbitrageGainEur)}</div><div className="l">{t("sim.arbitrage")}</div></div>
                )}
                <div className={`tile ${r.dynamicTariff.additionalAnnualBenefitEur > 0 ? "hl" : ""}`}>
                  <div className="v">{r.dynamicTariff.additionalAnnualBenefitEur >= 0 ? "+" : ""}{eur(r.dynamicTariff.additionalAnnualBenefitEur)}</div>
                  <div className="l">{t("sim.extraBenefit")}</div>
                </div>
              </div>
              <div className="muted" style={{ fontSize: 11.5 }}>
                {t("sim.dynNote")}
              </div>
            </div>
          )}
          {r.inverterGroups && r.inverterGroups.length > 0 && (
            <div className="card" style={{ marginBottom: 14 }}>
              <h3 style={{ marginBottom: 8 }}>{t("sim.inverterBanks")}</h3>
              <table className="tbl">
                <thead><tr><th>{t("sim.col.bank")}</th><th>{t("sim.col.inverter")}</th><th className="r">DC kWp</th><th className="r">AC kW</th><th className="r">DC/AC</th><th className="r">kWh/a</th><th>{t("sim.col.strings")}</th></tr></thead>
                <tbody>
                  {r.inverterGroups.map((g, i) => (
                    <tr key={i}>
                      <td><b>{g.label}</b></td>
                      <td>{g.count}× {g.inverterModel}</td>
                      <td className="r">{fmt(g.dcKwp, 1)}</td>
                      <td className="r">{fmt(g.acKw, 1)}</td>
                      <td className="r">{fmt(g.dcAcRatio, 2)}</td>
                      <td className="r">{fmt(g.annualAcKwh)}</td>
                      <td>{g.strings.stringCount} × {g.strings.modulesPerString} {g.strings.valid ? <span className="chip ok">{t("sim.ok")}</span> : <span className="chip bad">{t("sim.check")}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 8 }}>{t("sim.electricalDesign")}</h3>
            <div style={{ fontSize: 13 }}>
              {t("sim.stringsLine", { n: r.strings.stringCount, m: r.strings.modulesPerString, voc: fmt(r.strings.vocColdV, 1), ratio: fmt(r.strings.dcAcRatio, 2) })} {r.strings.valid ? <span className="chip ok">{t("sim.valid")}</span> : <span className="chip bad">{t("sim.invalid")}</span>}
            </div>
            {[...r.strings.violations, ...r.strings.warnings].map((m, i) => <div key={i} className="msg err" style={{ marginTop: 8 }}>{m}</div>)}
            {r.issues.filter((i) => i.severity !== "error").map((i, k) => <div key={k} className="msg info" style={{ marginTop: 8 }}>{i.message}</div>)}
          </div>
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 8 }}>{t("sim.costBreakdown")}</h3>
            <table className="tbl">
              <tbody>
                {r.cost.positions.map((p, i) => (
                  <tr key={i}><td>{p.label}</td><td className="r">{eur(p.amountEur)}</td></tr>
                ))}
                <tr><td><b>{t("sim.totalNet", { vat: r.cost.vatRate === 0 ? t("sim.vatCite") : "" })}</b></td><td className="r"><b>{eur(r.cost.totalNetEur)}</b></td></tr>
              </tbody>
            </table>
          </div>
          <ExportGateBanner gate={gate} />
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <button className="btn btn-o btn-sm" disabled={!!remedy} title={remedy ?? undefined} onClick={() => dl(`/projects/${detail.project.id}/designs/${design!.id}/bom.csv`, "BOM.csv")}>{t("sim.bomCsv")}{remedy ? ` — ${remedy}` : ""}</button>
            <button className="btn btn-o btn-sm" onClick={() => dl(`/projects/${detail.project.id}/designs/${design!.id}/cashflow.csv`, "Cashflow.csv")}>{t("sim.cashflowCsv")}</button>
          </div>
          <div className="msg info">{sim!.attribution}</div>
          <div className="msg info">{sim!.disclaimer}</div>
          <button className="btn btn-primary" onClick={onNext}>{t("sim.continueReg")}</button>
        </>
      )}
    </div>
  );
}

/* ----------------------------- Permits panel ----------------------------- */

/* ---------------------------- Procurement panel --------------------------- */

const ORDER_NEXT: Record<string, { to: string; labelKey: MessageKey }[]> = {
  DRAFT: [{ to: "ORDERED", labelKey: "proc.markOrdered" }, { to: "CANCELLED", labelKey: "proc.cancel" }],
  ORDERED: [{ to: "DELIVERED", labelKey: "proc.markDelivered" }, { to: "PARTIALLY_DELIVERED", labelKey: "proc.partialDelivery" }, { to: "CANCELLED", labelKey: "proc.cancel" }],
  PARTIALLY_DELIVERED: [{ to: "DELIVERED", labelKey: "proc.markDelivered" }],
  DELIVERED: [],
  CANCELLED: [],
};

function ProcurementPanel({ detail, gate, onNext }: { detail: ProjectDetail; gate: ExportGate | null; onNext: () => void }): JSX.Element {
  const t = useT();
  const design = detail.designs[detail.designs.length - 1];
  const remedy = gateRemedy(gate, t);
  const [orders, setOrders] = useState<ProcurementOrder[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierId, setSupplierId] = useState("");
  const [newSupplier, setNewSupplier] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  async function load(): Promise<void> {
    const [o, sp] = await Promise.all([api.projects.procurement(detail.project.id), api.projects.suppliers()]);
    setOrders(o);
    setSuppliers(sp);
  }
  useEffect(() => { void load(); }, [detail.project.id]);

  async function addSupplier(): Promise<void> {
    if (!newSupplier.trim()) return;
    const sp = await api.projects.createSupplier({ name: newSupplier.trim() });
    setNewSupplier("");
    setSuppliers((x) => [...x, sp]);
    setSupplierId(sp.id);
  }

  async function createOrder(): Promise<void> {
    if (!design) return;
    setBusy(true);
    setNote("");
    try {
      await api.projects.createOrder(detail.project.id, { designId: design.id, supplierId: supplierId || undefined });
      await load();
      setNote(t("proc.created"));
    } catch (e) {
      setNote(`${t("common.error")}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function transition(orderId: string, to: string): Promise<void> {
    await api.projects.updateOrder(detail.project.id, orderId, { status: to });
    await load();
  }

  const chipClass = (st: string): string =>
    st === "DELIVERED" ? "chip ok" : st === "CANCELLED" ? "chip bad" : st === "DRAFT" ? "chip" : "chip info";
  const dl = makeDl(setNote); // E47

  return (
    <div>
      <h1>{t("proc.title")}</h1>
      <div className="sub">{t("proc.hint")}</div>
      <ExportGateBanner gate={gate} />
      {note && <div className={`msg ${note.startsWith(t("common.error")) ? "err" : "ok"}`}>{note}</div>}
      <div className="card" style={{ marginBottom: 14 }}>
        <h3 style={{ marginBottom: 8 }}>{t("proc.newOrder")}</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} style={{ minWidth: 180 }}>
            <option value="">{t("proc.supplierOptional")}</option>
            {suppliers.map((sp) => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
          </select>
          <input placeholder={t("proc.addSupplierPlaceholder")} value={newSupplier} onChange={(e) => setNewSupplier(e.target.value)} style={{ width: 160 }} />
          <button className="btn btn-sm" onClick={() => void addSupplier()}>{t("proc.add")}</button>
          <button className="btn btn-primary" disabled={busy || !design} onClick={() => void createOrder()}>
            {busy ? <span className="spinner" /> : t("proc.createOrder")}
          </button>
        </div>
      </div>
      {orders.map((o) => (
        <div className="card" key={o.id} style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div>
              <b>{t("proc.order")} {o.id.slice(0, 8)}</b> <span className={chipClass(o.status)}>{o.status}</span>
              <div className="muted" style={{ fontSize: 11.5 }}>
                {o.items.length} {t("proc.positions")} · {eur(o.totalNetEur ?? 0)} {t("proc.net")} · {suppliers.find((sp) => sp.id === o.supplierId)?.name ?? t("proc.noSupplier")}
                {o.orderedAt ? ` · ${t("proc.ordered")} ${new Date(o.orderedAt).toLocaleDateString("de-DE")}` : ""}
                {o.deliveredAt ? ` · ${t("proc.delivered")} ${new Date(o.deliveredAt).toLocaleDateString("de-DE")}` : ""}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button className="btn btn-o btn-sm" onClick={() => setOpen(open === o.id ? null : o.id)}>{open === o.id ? t("proc.hide") : t("proc.positions")}</button>
              <button className="btn btn-o btn-sm" disabled={!!remedy} title={remedy ?? undefined} onClick={() => dl(`/projects/${detail.project.id}/procurement/${o.id}/po.csv`, `Bestellung-${o.id.slice(0, 8)}.csv`)}>{t("proc.poCsv")}</button>
              {ORDER_NEXT[o.status]?.map((tr) => (
                <button key={tr.to} className="btn btn-sm" onClick={() => void transition(o.id, tr.to)}>{t(tr.labelKey)}</button>
              ))}
            </div>
          </div>
          {open === o.id && (
            <table className="tbl" style={{ marginTop: 10 }}>
              <thead><tr><th>Item</th><th className="r">Qty</th><th className="r">Unit €</th><th className="r">Total €</th></tr></thead>
              <tbody>
                {o.items.map((it, i) => (
                  <tr key={i}>
                    <td>{it.label}<br /><span className="muted" style={{ fontSize: 10.5 }}>{it.componentType}</span></td>
                    <td className="r">{it.qty} {it.unit}</td>
                    <td className="r">{fmt(it.unitEur, 2)}</td>
                    <td className="r">{eur(it.qty * it.unitEur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
      {orders.length === 0 && <div className="muted" style={{ fontSize: 12.5 }}>{t("proc.noOrders")}</div>}
      <div style={{ marginTop: 12 }}>
        <button className="btn" onClick={onNext}>{t("proc.continueInstall")}</button>
      </div>
    </div>
  );
}

/* ---------------------------- Installation panel -------------------------- */

const TASK_STATUS: { id: string; labelKey: MessageKey }[] = [
  { id: "OPEN", labelKey: "inst.status.OPEN" },
  { id: "IN_PROGRESS", labelKey: "inst.status.IN_PROGRESS" },
  { id: "DONE", labelKey: "inst.status.DONE" },
  { id: "BLOCKED", labelKey: "inst.status.BLOCKED" },
];

function InstallationPanel({ detail, onReload }: { detail: ProjectDetail; onReload: () => Promise<void> }): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  /** E44: locale-PINNED date rendering (DD.MM.YYYY for DE) — never the browser locale. */
  const fmtDate = (iso: string | null | undefined): string =>
    iso ? new Date(iso).toLocaleDateString(locale === "de" ? "de-DE" : "en-GB") : "";
  const [view, setView] = useState<InstallationView | null>(null);
  const [crew, setCrew] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);
  const [comm, setComm] = useState({ electricianName: "", electricianRegistrationNo: "", meterNumber: "", mastrNumber: "" });
  const [note, setNote] = useState("");

  async function load(): Promise<void> {
    const v = await api.projects.installation(detail.project.id);
    setView(v);
    if (v.plan) {
      setCrew(v.plan.crewName ?? "");
      setStart(v.plan.scheduledStart?.slice(0, 10) ?? "");
      setEnd(v.plan.scheduledEnd?.slice(0, 10) ?? "");
    }
    if (v.commissioning) {
      setComm({
        electricianName: v.commissioning.electricianName ?? "",
        electricianRegistrationNo: v.commissioning.electricianRegistrationNo ?? "",
        meterNumber: v.commissioning.meterNumber ?? "",
        mastrNumber: v.commissioning.mastrNumber ?? "",
      });
    }
  }
  useEffect(() => { void load(); }, [detail.project.id]);

  async function savePlan(): Promise<void> {
    setBusy(true);
    try {
      await api.projects.upsertInstallationPlan(detail.project.id, {
        crewName: crew || null,
        scheduledStart: start || null,
        scheduledEnd: end || null,
      });
      await load();
      await onReload(); // E44: the lifecycle badge advances — refresh it
      setNote(t("inst.planSaved"));
    } catch (e) {
      setNote(`${t("common.error")}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function setTask(taskId: string, status: string): Promise<void> {
    await api.projects.updateInstallationTask(detail.project.id, taskId, { status });
    await load();
    await onReload(); // E44: INSTALLING/COMMISSIONING advance shows in the footer badge
  }

  async function saveCommissioning(markDone: boolean): Promise<void> {
    setBusy(true);
    try {
      await api.projects.saveCommissioning(detail.project.id, {
        ...comm,
        ...(markDone ? { commissionedAt: new Date().toISOString() } : {}),
      });
      await load();
      await onReload(); // E44: OPERATING advance shows in the footer badge
      setNote(markDone ? t("inst.commissioningDone") : t("inst.commissioningSaved"));
    } finally {
      setBusy(false);
    }
  }

  const doneCount = view?.tasks.filter((x) => x.status === "DONE").length ?? 0;

  return (
    <div>
      <h1>{t("inst.title")}</h1>
      <div className="sub">{t("inst.hint")}</div>
      {note && <div className={`msg ${note.startsWith(t("common.error")) ? "err" : "ok"}`}>{note}</div>}
      <div className="card" style={{ marginBottom: 14 }}>
        <h3 style={{ marginBottom: 8 }}>{t("inst.schedule")}</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
          <div className="field"><label>{t("inst.crew")}</label><input value={crew} onChange={(e) => setCrew(e.target.value)} placeholder="Team Nord" style={{ width: 150 }} /></div>
          {/* E44: the native date input renders in the BROWSER locale (02/01/2026 ambiguity
              trap) — an unambiguous locale-pinned label always states what is stored */}
          <div className="field"><label>{t("inst.start")} <span className="muted">({t("inst.dateFormat")})</span></label>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            {start && <span className="muted" style={{ fontSize: 11.5 }} data-testid="start-pinned">{fmtDate(start)}</span>}</div>
          <div className="field"><label>{t("inst.end")} <span className="muted">({t("inst.dateFormat")})</span></label>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            {end && <span className="muted" style={{ fontSize: 11.5 }} data-testid="end-pinned">{fmtDate(end)}</span>}</div>
          <button className="btn btn-primary" disabled={busy} onClick={() => void savePlan()}>
            {busy ? <span className="spinner" /> : view?.plan ? t("inst.updatePlan") : t("inst.createPlan")}
          </button>
        </div>
      </div>
      {view?.plan && (
        <div className="card" style={{ marginBottom: 14 }}>
          <h3 style={{ marginBottom: 8 }}>{t("inst.taskBoard")} <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>· {doneCount}/{view.tasks.length} {t("inst.done")}</span></h3>
          <table className="tbl">
            <tbody>
              {view.tasks.map((tk) => (
                <tr key={tk.id} style={tk.status === "DONE" ? { opacity: 0.55 } : undefined}>
                  <td>
                    <b>{tk.title}</b>
                    <br /><span className="muted" style={{ fontSize: 10.5 }}>
                      {tk.category}
                      {tk.scheduledFor ? ` · ${t("inst.plannedFor")} ${fmtDate(tk.scheduledFor)}` : ""}
                      {tk.assignee ? ` · ${tk.assignee}` : ""}
                      {tk.doneAt ? ` · ✓ ${fmtDate(tk.doneAt)}` : ""}
                    </span>
                  </td>
                  <td className="r" style={{ width: 150 }}>
                    <select value={tk.status} onChange={(e) => void setTask(tk.id, e.target.value)}>
                      {TASK_STATUS.map((st) => <option key={st.id} value={st.id}>{t(st.labelKey)}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="card">
        <h3 style={{ marginBottom: 8 }}>{t("inst.commissioning")} {view?.commissioning?.commissionedAt && <span className="chip ok">{t("inst.commissioned")} {fmtDate(view.commissioning.commissionedAt)}</span>}</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div className="field"><label>{t("inst.electrician")}</label><input value={comm.electricianName} onChange={(e) => setComm({ ...comm, electricianName: e.target.value })} placeholder="Elektro Huber GmbH" /></div>
          <div className="field"><label>{t("inst.registrationNo")}</label><input value={comm.electricianRegistrationNo} onChange={(e) => setComm({ ...comm, electricianRegistrationNo: e.target.value })} placeholder="HWK-…" /></div>
          <div className="field"><label>{t("inst.meterNumber")}</label><input value={comm.meterNumber} onChange={(e) => setComm({ ...comm, meterNumber: e.target.value })} /></div>
          <div className="field"><label>{t("inst.mastrNumber")}</label><input value={comm.mastrNumber} onChange={(e) => setComm({ ...comm, mastrNumber: e.target.value })} placeholder="SEE…" /></div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button className="btn" disabled={busy} onClick={() => void saveCommissioning(false)}>{t("inst.save")}</button>
          <button className="btn btn-primary" disabled={busy} onClick={() => void saveCommissioning(true)}>{t("inst.markCommissioned")}</button>
        </div>
      </div>
    </div>
  );
}

function PermitsPanel({ detail, onReload, gate, onNext }: { detail: ProjectDetail; onReload: () => Promise<void>; gate: ExportGate | null; onNext: () => void }): JSX.Element {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const dl = makeDl(setNote); // E47
  const remedy = gateRemedy(gate, t);
  const kindLabel = (kind: string): string => {
    const key = `perm.kind.${kind}` as MessageKey;
    const s = t(key);
    return s === key ? kind : s; // unknown kinds fall back to the raw enum
  };

  async function generate(): Promise<void> {
    setBusy(true);
    setNote("");
    try {
      await api.projects.generatePermits(detail.project.id);
      await onReload();
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1>{t("perm.title")}</h1>
      <div className="sub">{t("perm.hint")}</div>
      {detail.permitTasks.length === 0 ? (
        <>
          {note && <div className="msg err">{note}</div>}
          <button className="btn btn-primary" disabled={busy} onClick={() => void generate()}>
            {busy ? <span className="spinner" /> : t("perm.generate")}
          </button>
        </>
      ) : (
        <>
          {detail.permitTasks.map((pt) => (
            <div key={pt.id} className={`check ${pt.status === "DONE" || pt.status === "APPROVED" ? "donec" : ""}`}>
              <div className="cb" onClick={() => void api.projects.updatePermit(detail.project.id, pt.id, pt.status === "DONE" ? "OPEN" : "DONE").then(onReload)}>
                {pt.status === "DONE" || pt.status === "APPROVED" ? "✓" : ""}
              </div>
              <div className="t"><b>{kindLabel(pt.kind)}</b><span>{pt.notes}</span></div>
            </div>
          ))}
          {detail.designs.length > 0 && (
            <div className="card" style={{ marginTop: 12 }}>
              <h3 style={{ marginBottom: 6 }}>{t("perm.pkgTitle")}</h3>
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                {t("perm.pkgHint")}
              </div>
              <ExportGateBanner gate={gate} />
              {note && <div className="msg err">{note}</div>}
              <button
                className="btn btn-primary"
                disabled={!!remedy}
                title={remedy ?? undefined}
                onClick={() => {
                  const d = detail.designs[detail.designs.length - 1]!;
                  dl(`/projects/${detail.project.id}/designs/${d.id}/permit-package.pdf`, "Netzanmeldung.pdf");
                }}
              >
                {t("perm.download")}{remedy ? ` — ${remedy}` : ""}
              </button>
            </div>
          )}
          <button className="btn btn-primary" onClick={onNext} style={{ marginTop: 12 }}>{t("perm.continueProposals")}</button>
        </>
      )}
    </div>
  );
}

/* ---------------------------- Proposals panel ---------------------------- */

function ProposalsPanel({ detail, sim, onReload, gate }: { detail: ProjectDetail; sim: SimulationResponse | null; onReload: () => Promise<void>; gate: ExportGate | null }): JSX.Element {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const dl = makeDl(setNote); // E47 (proposal PDF stays inside the demo boundary — watermarked — so no disable; failures now surface)
  const [signUrl, setSignUrl] = useState("");
  const design = detail.designs[detail.designs.length - 1];

  async function create(): Promise<void> {
    if (!design) return;
    setBusy(true);
    setNote("");
    try {
      await api.projects.createProposal(detail.project.id, design.id);
      await onReload();
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function send(proposalId: string): Promise<void> {
    setNote("");
    try {
      const res = await api.projects.sendProposal(detail.project.id, proposalId);
      setSignUrl(`${window.location.origin}/v1/public/sign${res.signUrl.replace("/sign", "")}`);
      await onReload();
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e));
    }
  }

  const chip = (s: string) => (s === "ACCEPTED" ? "ok" : s === "SENT" ? "info" : s === "REJECTED" || s === "EXPIRED" ? "bad" : "");

  return (
    <div>
      <h1>{t("prop.title")}</h1>
      <div className="sub">{t("prop.hint")}</div>
      {note && <div className="msg err">{note}</div>}
      <button className="btn btn-primary" disabled={busy || !design || !sim} onClick={() => void create()} style={{ marginBottom: 14 }}>
        {busy ? <span className="spinner" /> : t("prop.create")}
      </button>
      {!sim && <span className="muted" style={{ marginLeft: 10 }}>{t("prop.simFirst")}</span>}
      {signUrl && (
        <div className="msg ok">
          {t("prop.signingLink")} <code style={{ fontSize: 11 }}>{signUrl}</code>{" "}
          <button className="btn btn-sm" onClick={() => void navigator.clipboard.writeText(signUrl)}>{t("prop.copy")}</button>
        </div>
      )}
      {detail.proposals.length > 0 && (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>{t("prop.col.number")}</th><th>{t("prop.col.status")}</th><th className="r">{t("prop.col.net")}</th><th className="r">{t("prop.col.gross")}</th><th>{t("prop.col.validUntil")}</th><th></th></tr></thead>
            <tbody>
              {detail.proposals.map((p) => (
                <tr key={p.id}>
                  <td><b>{p.number}</b></td>
                  <td>
                    <span className={`chip ${chip(p.status)}`}>{p.status}</span>
                    {p.signedAt && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>✍ {new Date(p.signedAt).toLocaleDateString("de-DE")}</span>}
                  </td>
                  <td className="r">{eur(p.totalNetEur)}</td>
                  <td className="r">{eur(p.totalGrossEur)}</td>
                  <td>{p.validUntil ? new Date(p.validUntil).toLocaleDateString("de-DE") : "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="btn btn-sm btn-o" onClick={() => dl(`/projects/${detail.project.id}/proposals/${p.id}/pdf`, `Angebot-${p.number}.pdf`)}>{t("prop.pdf")}</button>{" "}
                    {p.status === "DRAFT" && <button className="btn btn-sm" onClick={() => void send(p.id)}>{t("prop.send")}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
